<script setup>
// =============================================================================
// FangateLayout.vue — Stage B: 4-column shell
//
// Route:  /app/accounts/:accountId/dashboard  (mounted as name: 'home')
// Parent: none (top-level route, independent of Chatwoot's Dashboard.vue shell)
//
// Columns (left → right):
//   1. Nav sidebar  — 260 px, bg-app
//   2. List pane    — 270 px, bg-middle, 2-layer slide (accounts → chats)
//   3. Chat area    — flex-1, bg-main
//   4. Fangate panel — 280 px collapsible, bg-fangate
//
// Subsequent stages fill each column with real components (C → sidebar,
// D → list pane, E → panel, F → topbar).
// =============================================================================

import { ref, computed, onMounted, onUnmounted, onErrorCaptured, reactive, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import InboxMembersAPI from 'dashboard/api/inboxMembers';
import FangateSidebar   from './FangateSidebar.vue';
import FangateListPane  from './FangateListPane.vue';
import FangateChatArea         from './FangateChatArea.vue';
import FangateSettings         from './FangateSettings.vue';
import FangateAgentsManagement from './FangateAgentsManagement.vue';
import FangatePanel     from './FangatePanel.vue';
import FangateVaultView from './FangateVaultView.vue';
import FangateTransactions from './components/FangateTransactions.vue';
import FangateAgentsPerformance from './components/FangateAgentsPerformance.vue';
import FangateBoundary  from './FangateBoundary.vue';
import FangateNewChatModal from './components/FangateNewChatModal.vue';
import FangateConfirmHost from './components/FangateConfirmHost.vue';
// One-time "what's new" pop-up. FangateLayout is a TOP-LEVEL route (not a child
// of AppContainer/Dashboard.vue), so the popup must be mounted here too — this
// is where users land after login.
import AnnouncementPopup from 'dashboard/components/app/AnnouncementPopup.vue';
import ConversationAPI  from 'dashboard/api/inbox/conversation';
import FangateAPI       from 'dashboard/api/fangate';

// ---- Error overlay (diagnostic — surfaces a child error visibly) ----------
const errorInfo = ref(null);
onErrorCaptured((err, _instance, info) => {
  errorInfo.value = {
    message: err?.message ?? String(err),
    stack:   (err?.stack ?? '').split('\n').slice(0, 6).join('\n'),
    info,
  };
  // Don't propagate — keep the page rendering whatever else it can
  return false;
});

const store     = useStore();
const route     = useRoute();
const router    = useRouter();
// Notification bell → existing Chatwoot notifications page. Account id comes
// from the dashboard route param (same shape Chatwoot uses internally).
// Notification bell dropdown — inline panel (no separate page nav). Toggles
// open on bell click, lists the latest 6 notifications + a Clear button.
const bellOpen = ref(false);
const unreadCount = computed(() => store.getters['notifications/getUnreadCount'] || 0);
const unreadBadge = computed(() => {
  const n = unreadCount.value;
  if (!n) return '';
  return n < 100 ? String(n) : '99+';
});
const latestNotifications = computed(() => {
  const list = store.getters['notifications/getNotifications'] || [];
  return list.slice(0, 6);
});
const refreshUnread = () => { store.dispatch('notifications/unReadCount').catch(() => {}); };
// Fetch INCLUDING read notifications (status:'read' flips the finder off its
// unread-only default) so opening the bell — which marks everything read — does
// not empty the list. Items persist until "Clear".
const refreshNotifications = () => { store.dispatch('notifications/index', { page: 1, status: 'read', sortOrder: 'desc' }).catch(() => {}); };
const openBell = async () => {
  bellOpen.value = !bellOpen.value;
  if (!bellOpen.value) return;
  // 1) Mark everything read FIRST → clears the red unread badge.
  //    (UPDATE_ALL_NOTIFICATIONS only flips read_at, so we refresh the count
  //    below to actually zero the badge.)
  try { await store.dispatch('notifications/readAll'); } catch (_) { /* ignore */ }
  // 2) Fetch the list INCLUDING read items so they persist (the finder hides
  //    read by default). "Clear" is the only thing that removes them.
  refreshNotifications();
  // 3) Pull the now-zero unread count so the badge disappears.
  refreshUnread();
};
const closeBell = () => { bellOpen.value = false; };
// Clear = permanently DELETE all notifications in one call (deleteAll →
// type:'all'). The old read-all-then-delete-read two-step left items behind
// when the read state hadn't persisted server-side yet.
const clearAllNotifications = async () => {
  try {
    await store.dispatch('notifications/deleteAll');
  } catch (_) { /* swallow — UI refreshes below */ }
  refreshUnread();
  refreshNotifications();
  bellOpen.value = false;
};
// Click a single notification → jump to its conversation (if any). Notifications
// PERSIST until "Clear" is pressed — clicking never deletes. Announcements are
// informational, so they have no target to open.
const onClickNotification = (notification) => {
  if (notification.notification_type === 'system_announcement') return;
  const convId = notification.primary_actor?.id;
  if (!convId) return;
  bellOpen.value = false;
  router.push(`/app/accounts/${accountId.value}/conversations/${convId}`);
};
const formatNotificationTime = ts => {
  if (!ts) return '';
  const date = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return date.toLocaleDateString();
};
let unreadTimerId = null;
onMounted(() => {
  refreshUnread();
  refreshNotifications();
  unreadTimerId = setInterval(() => { refreshUnread(); if (bellOpen.value) refreshNotifications(); }, 30 * 1000);
});
onUnmounted(() => { if (unreadTimerId) clearInterval(unreadTimerId); });
const accountId = computed(() => Number(route.params.accountId));

// ---- Responsive: mobile (single-pane drill-down) vs desktop (4 columns) -----
// Drives the .is-mobile root class; ALL mobile layout is scoped under it in CSS,
// so desktop is untouched. Breakpoint matches the mockup (phones + small tablets).
const isMobile = ref(false);
let mqMobile = null;
const onMqMobileChange = e => { isMobile.value = e.matches; };
onMounted(() => {
  mqMobile = window.matchMedia('(max-width: 768px)');
  isMobile.value = mqMobile.matches;
  if (mqMobile.addEventListener) mqMobile.addEventListener('change', onMqMobileChange);
  else if (mqMobile.addListener) mqMobile.addListener(onMqMobileChange);  // older Safari
});
onUnmounted(() => {
  if (!mqMobile) return;
  if (mqMobile.removeEventListener) mqMobile.removeEventListener('change', onMqMobileChange);
  else if (mqMobile.removeListener) mqMobile.removeListener(onMqMobileChange);
});

// Suppress the URL-sync watcher while we're restoring state from the URL on
// mount — otherwise the watcher would fire on each ref assignment and stomp
// over the very query params we're trying to read.
let restoring = true;

// ---- Fetch inboxes + agents on mount ----------------------------------------
const closeAgentPicker = () => { agentPicker.show = false; };

// ---- Unread / total counts per inbox (sidebar consumes this) -----------------
// PRIMARY source: Chatwoot's Vuex `getAllConversations` getter. Every
// ADD_MESSAGE / UPDATE_MESSAGE_UNREAD_COUNT / etc. mutation re-renders
// these computeds the same frame the chat list updates. No polling lag.
//
// FALLBACK source: REST fetch on mount + 30s safety-net polling. Only
// covers inboxes whose conversations haven't been pulled into Vuex yet —
// the merge below prefers the live count whenever Vuex knows about it.
const restUnreadByInbox         = ref({});  // chats only — populated by refreshInboxCounts
const restUnreadByInboxChannels = ref({});  // group/supergroup/channel only
const restLastActivityByInbox   = ref({});  // max activity ts per inbox (covers inboxes not in the Vuex store)
const restTotalByInbox          = ref({});
const restTotalByInboxChannels  = ref({});

// Live count derived straight from the Vuex store. Recomputes instantly
// whenever any conversation's unread_count mutates.
// The sidebar pill counts CHATS with unread messages, not the total number
// of unread messages — matches WhatsApp / Telegram conventions where the
// inbox badge says "3 chats are unread" rather than "12 messages waiting".
// The chat-list pills still show per-conversation message counts.
// Only open conversations are eligible (same filter as the REST call).
//
// Split by conversation kind so the sidebar CHATS section sorts/badges by
// DM unread only, and the CHANNELS section sorts/badges by group/channel
// unread only — an unread group message must not push an account up in
// the CHATS list, and vice versa. Discriminator is the same persisted
// `additional_attributes.chat_type` used by FangateListPane.
const isChannelConv = (c) => {
  const t = c?.additional_attributes?.chat_type;
  return t === 'group' || t === 'supergroup' || t === 'channel';
};

const liveUnreadByInbox = computed(() => {
  const result = {};
  const convs = store.getters.getAllConversations || [];
  for (const c of convs) {
    if (!c?.inbox_id) continue;
    if (c.status && c.status !== 'open') continue;
    if (isChannelConv(c)) continue;
    if (c.muted) continue; // muted conversations contribute zero unread (no pill)
    if (!((c.unread_count || 0) > 0)) continue;
    result[c.inbox_id] = (result[c.inbox_id] || 0) + 1;
  }
  return result;
});
const liveUnreadByInboxChannels = computed(() => {
  const result = {};
  const convs = store.getters.getAllConversations || [];
  for (const c of convs) {
    if (!c?.inbox_id) continue;
    if (c.status && c.status !== 'open') continue;
    if (!isChannelConv(c)) continue;
    if (c.muted) continue; // muted conversations contribute zero unread (no pill)
    if (!((c.unread_count || 0) > 0)) continue;
    result[c.inbox_id] = (result[c.inbox_id] || 0) + 1;
  }
  return result;
});
const liveTotalByInbox = computed(() => {
  const result = {};
  const convs = store.getters.getAllConversations || [];
  for (const c of convs) {
    if (!c?.inbox_id) continue;
    if (c.status && c.status !== 'open') continue;
    if (isChannelConv(c)) continue;
    result[c.inbox_id] = (result[c.inbox_id] || 0) + 1;
  }
  return result;
});
const liveTotalByInboxChannels = computed(() => {
  const result = {};
  const convs = store.getters.getAllConversations || [];
  for (const c of convs) {
    if (!c?.inbox_id) continue;
    if (c.status && c.status !== 'open') continue;
    if (!isChannelConv(c)) continue;
    result[c.inbox_id] = (result[c.inbox_id] || 0) + 1;
  }
  return result;
});

// Merged: live wins per-inbox if the user has interacted with that inbox
// in this session (Vuex has its conversations). Otherwise REST fills in.
// Helper avoids duplicating the same merge dance four times (chats/channels
// × unread/total).
const mergeLiveOverRest = (restMap, liveMap) => {
  const merged = { ...(restMap || {}) };
  const liveInboxIds = new Set();
  const convs = store.getters.getAllConversations || [];
  for (const c of convs) if (c?.inbox_id) liveInboxIds.add(c.inbox_id);
  for (const iid of liveInboxIds) {
    merged[iid] = liveMap[iid] || 0;
  }
  return merged;
};
const unreadByInbox = computed(() =>
  mergeLiveOverRest(restUnreadByInbox.value, liveUnreadByInbox.value)
);
const unreadByInboxChannels = computed(() =>
  mergeLiveOverRest(restUnreadByInboxChannels.value, liveUnreadByInboxChannels.value)
);
const totalByInbox = computed(() =>
  mergeLiveOverRest(restTotalByInbox.value, liveTotalByInbox.value)
);
const totalByInboxChannels = computed(() =>
  mergeLiveOverRest(restTotalByInboxChannels.value, liveTotalByInboxChannels.value)
);

// Per-inbox "last activity" timestamp (UNIX seconds, matching Chatwoot's
// shape on conversations). Used by the sidebar to sort accounts by recency
// so the just-touched inbox bubbles to the top — the same pattern WhatsApp/
// Telegram themselves use. Computed from the Vuex conversations list and
// re-flushes via the same ADD_MESSAGE/UPDATE_MESSAGE mutation subscription
// that already drives unreadByInbox.
// Activity timestamp of ONE conversation = the created_at of its last REAL
// (incoming/outgoing) message. Crucially NOT last_activity_at / updated_at —
// Chatwoot bumps those on reads, assignments, automation runs etc., which would
// (and did) drag a long-quiet account back to the top just because the agent
// opened or read a different chat in it.
const toEpoch = v => {
  if (!v) return 0;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : Date.parse(v) / 1000;
  }
  return Number.isFinite(v) ? v : 0;
};
const convActivityTs = c => {
  if (Array.isArray(c?.messages) && c.messages.length) {
    for (let i = c.messages.length - 1; i >= 0; i -= 1) {
      const m = c.messages[i];
      if (m && (m.message_type === 0 || m.message_type === 1)) {
        const ts = toEpoch(m.created_at);
        if (ts > 0) return ts;
      }
    }
  }
  const lnam = toEpoch(c?.last_non_activity_message?.created_at);
  if (lnam > 0) return lnam;
  return toEpoch(c?.created_at);
};

// LIVE source: conversations currently in Vuex (covers inboxes the user has
// opened; updates instantly on ADD_MESSAGE).
const liveLastActivityByInbox = computed(() => {
  const out = {};
  for (const c of (store.getters.getAllConversations || [])) {
    const iid = c?.inbox_id;
    if (!iid) continue;
    const n = convActivityTs(c);
    if (n > (out[iid] || 0)) out[iid] = n;
  }
  return out;
});

// Merge REST (covers ALL inboxes, incl. ones never opened this session) with
// LIVE, taking the MAX per inbox. Without the REST source, inboxes whose
// conversations aren't in the Vuex store had lastActivity = 0 → the sidebar's
// Activity sort couldn't tell them apart and fell back to the unread/alpha
// tie-breaker, so OPENING (reading) a chat dropped its account down the list.
// With a real timestamp, reading doesn't change activity → the account stays
// put; it only moves when another account gets a newer in/out message. (#157)
const lastActivityByInbox = computed(() => {
  const out = { ...restLastActivityByInbox.value };
  for (const [iid, ts] of Object.entries(liveLastActivityByInbox.value)) {
    if (ts > (out[iid] || 0)) out[iid] = ts;
  }
  return out;
});

// Debounced REST refresh — feeds restUnreadByInbox for inboxes the user
// hasn't visited yet. Still triggered on mount, 30s interval, and on
// Vuex mutations (belt-and-suspenders).
let refreshDebounce = null;
const refreshInboxCounts = async () => {
  if (refreshDebounce) clearTimeout(refreshDebounce);
  refreshDebounce = setTimeout(async () => {
    refreshDebounce = null;
    try {
      const resp = await ConversationAPI.get({
        assigneeType: 'all',
        status: 'open',
        page: 1,
      });
      const convs = resp?.data?.data?.payload ?? [];
      const u = {}, t = {}, uCh = {}, tCh = {}, la = {};
      for (const c of convs) {
        const iid = c.inbox_id;
        if (!iid) continue;
        // Track most-recent activity per inbox so the sidebar's Activity sort has
        // a real timestamp even for inboxes the user hasn't opened this session.
        const ts = convActivityTs(c);
        if (ts > (la[iid] || 0)) la[iid] = ts;
        // Split into chats vs group/supergroup/channel — chat_type lives in
        // additional_attributes (persisted by Telegram::IncomingMessageService).
        // No chat_type → treat as DM (legacy / pre-migration rows).
        const isCh = isChannelConv(c);
        // Count CHATS with unread, not the total unread-message sum (see
        // liveUnreadByInbox above for the rationale). Muted conversations are
        // excluded here too (mirrors the live maps' `if (c.muted) continue`) so
        // a muted chat in a not-yet-opened inbox doesn't inflate the sidebar /
        // account badge via the REST path. The serializer exposes `muted`.
        const hasUnread = !c.muted && (c.unread_count || 0) > 0;
        if (isCh) {
          tCh[iid] = (tCh[iid] || 0) + 1;
          if (hasUnread) uCh[iid] = (uCh[iid] || 0) + 1;
        } else {
          t[iid] = (t[iid] || 0) + 1;
          if (hasUnread) u[iid] = (u[iid] || 0) + 1;
        }
      }
      // Assign new objects (not in-place mutation) so any consumer using
      // .value gets a fresh reference and re-renders cleanly.
      restUnreadByInbox.value         = u;
      restUnreadByInboxChannels.value = uCh;
      restTotalByInbox.value          = t;
      restTotalByInboxChannels.value  = tCh;
      restLastActivityByInbox.value   = la;
    } catch (_) { /* leave previous values on error */ }
  }, 250);
};

let countsRefreshTimer = null;
let storeUnsubscribe   = null;

// Refresh unread counts on tab-focus so users coming back to the tab see
// fresh counts immediately instead of after the next poll tick.
const onWindowFocus = () => { refreshInboxCounts(); };

// Chatwoot's existing Action Cable wiring commits these Vuex mutations
// whenever the conversation list changes (new message arrives, conversation
// gets resolved, unread count gets cleared, etc.). Subscribing to them
// gives us near-real-time updates without polling and without setting up
// our own socket. The debounce in refreshInboxCounts collapses bursts.
const RELEVANT_MUTATIONS = new Set([
  'ADD_MESSAGE',
  'UPDATE_CONVERSATION',
  'SET_CONVERSATION_LAST_SEEN',
  'UPDATE_CONVERSATION_LAST_ACTIVITY',
  // The mutation Chatwoot actually fires when an agent reads a conversation
  // (markMessagesRead action commits it). Without this, the unread counter
  // ticks UP from ADD_MESSAGE but never ticks back DOWN.
  'UPDATE_MESSAGE_UNREAD_COUNT',
]);

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('inboxes/get'),
      store.dispatch('agents/get'),
      store.dispatch('labels/get'),
      // Integration apps incl. the `openai` hook — drives the ChatGPT gate on
      // the chat composer's "Suggest reply" button (useChatGptState). Cheap,
      // fire-and-forget within the batch.
      store.dispatch('integrations/get'),
    ]);
  } catch (_) {}
  // Hydrate inbox->agents now that inboxes are loaded, so isAgentAssigned +
  // the account-by-agent filter reflect real memberships (fire-and-forget).
  hydrateInboxAgentMap();
  // Restore last view / inbox / conversation from URL query params (F5 survival).
  // Done AFTER inboxes are loaded so any UI that reacts to selectedInboxId
  // can resolve the inbox object immediately.
  await restoreFromUrl();
  restoring = false;
  // Fetch counts AFTER inboxes are loaded
  refreshInboxCounts();
  // Polling stays as a safety net but the primary update path is the
  // Vuex subscriber below (instant, debounced). 30s is fine as the
  // belt-and-suspenders interval.
  countsRefreshTimer = setInterval(refreshInboxCounts, 30000);
  // Subscribe to Chatwoot's existing conversation-related mutations. They
  // already fire when Action Cable receives a new message / read receipt /
  // status change → we get near-real-time unread updates for free.
  storeUnsubscribe = store.subscribe(mutation => {
    if (RELEVANT_MUTATIONS.has(mutation.type)) refreshInboxCounts();
  });
  document.addEventListener('click', closeAgentPicker);
  document.addEventListener('click', closeUserMenu);
  window.addEventListener('focus', onWindowFocus);
});
onUnmounted(() => {
  document.removeEventListener('click', closeAgentPicker);
  document.removeEventListener('click', closeUserMenu);
  window.removeEventListener('focus', onWindowFocus);
  if (countsRefreshTimer) clearInterval(countsRefreshTimer);
  if (storeUnsubscribe) storeUnsubscribe();
  if (refreshDebounce) clearTimeout(refreshDebounce);
});
// (selectedInboxId watcher lives further down — it needs the ref to be
// declared first to avoid a TDZ ReferenceError at setup time.)

// ---- Real data from Vuex ----------------------------------------------------
const allInboxes = computed(() => store.getters['inboxes/getInboxes'] ?? []);

// =============================================================================
// Co-creator consent requests — a SEPARATE indicator next to the bell (kept
// apart from Chatwoot chat notifications on purpose). Counts pending incoming
// tagging requests across the user's connected Fangate vaults; clicking opens
// that vault's Collaborations module where Accept/Decline live. The dot
// persists until the request is accepted or declined (the count re-polls).
// =============================================================================
// Plain function (not a computed) so every poll re-reads the localStorage
// connection flags, which aren't reactive.
const getConnectedVaultInboxes = () =>
  (allInboxes.value || []).filter(ix =>
    (ix.channel_type === 'Channel::Telegram' || ix.channel_type === 'Channel::TelegramUser') &&
    FangateAPI.isInboxConnected(ix.id)
  );

// Reports → Account performance: connected-account list shown in Col 2 (like Chats).
const connectedFangateAccounts = computed(() =>
  getConnectedVaultInboxes()
    .map(ix => ({ id: ix.id, name: ix.name }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
);
const acctInitials = name => ((name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?');
// [{ inbox, tags: [pendingTag, …] }] for vaults with pending incoming tags.
const consentRequests   = ref([]);
const consentOpen       = ref(false);   // popover open?
const respondingTagId   = ref(null);    // tag currently being accepted
const consentPendingCount = computed(() =>
  consentRequests.value.reduce((n, r) => n + (r.tags?.length || 0), 0)
);
const consentBadge = computed(() => {
  const n = consentPendingCount.value;
  return n ? (n < 100 ? String(n) : '99+') : '';
});
// Flattened rows for the popover list: { inbox, tag }.
const consentTagRows = computed(() =>
  consentRequests.value.flatMap(r => (r.tags || []).map(tag => ({ inbox: r.inbox, tag })))
);
const taggerLabel = tag => {
  const u = tag?.tagger || {};
  const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return full || u.display_name || u.email || 'Someone';
};

let consentPollTimer = null;
const pollConsentRequests = async () => {
  const vaults = getConnectedVaultInboxes();
  if (!vaults.length) { consentRequests.value = []; return; }
  const rows = await Promise.all(vaults.map(async ix => {
    try {
      const tags = await FangateAPI.listIncomingConsentTags({ inboxId: ix.id });
      const pending = (tags || []).filter(t => t.status === 'pending');
      return pending.length ? { inbox: ix, tags: pending } : null;
    } catch (_) {
      return null;   // API-key 403 / network → silently skip this vault
    }
  }));
  consentRequests.value = rows.filter(Boolean);
};

const toggleConsentPopover = () => {
  consentOpen.value = !consentOpen.value;
  if (consentOpen.value) pollConsentRequests();   // refresh on open
};
const closeConsentPopover = () => { consentOpen.value = false; };

// Accept → fire the consent straight to Fangate (PATCH /consent-tags/:id), then
// re-poll so the badge updates. Demo tags (negative id) are simulated locally.
const acceptConsentTag = async (inbox, tag) => {
  if (respondingTagId.value) return;
  respondingTagId.value = tag.id;
  try {
    if (tag.id < 0) {
      consentRequests.value = consentRequests.value
        .map(r => ({ ...r, tags: (r.tags || []).filter(t => t.id !== tag.id) }))
        .filter(r => r.tags.length);
    } else {
      await FangateAPI.respondToConsentTag(tag.id, 'accept', { inboxId: inbox.id });
      await pollConsentRequests();
    }
    if (!consentPendingCount.value) consentOpen.value = false;
  } catch (e) {
    useAlert(e?.message || 'Could not accept the tag.');
  } finally {
    respondingTagId.value = null;
  }
};

// Decline → open that vault's Collaborations module (decline happens there).
const declineConsentTag = inbox => {
  consentOpen.value = false;
  openVaultCollaborations(inbox);
};

// "click here to see further details" → same Collaborations module, but framed
// as a look (not a decline), so the user can review before responding.
const openConsentDetails = inbox => {
  consentOpen.value = false;
  openVaultCollaborations(inbox);
};

// Navigate into a vault's Collaborations module via the per-account UI snapshot
// the vault restores from on account-switch (key + schema owned by VaultView).
const openVaultCollaborations = inbox => {
  if (!inbox?.id) return;
  try {
    const key = `fangate_vault_ui_v1_${inbox.id}`;
    const prev = JSON.parse(localStorage.getItem(key) || '{}');
    localStorage.setItem(key, JSON.stringify({ ...prev, module: 'collaborations' }));
  } catch (_) { /* storage unavailable — vault still opens, just on last module */ }
  handleSelectFangateAccount(inbox);
};

onMounted(() => {
  pollConsentRequests();
  consentPollTimer = setInterval(pollConsentRequests, 5 * 60 * 1000);
});
onUnmounted(() => { if (consentPollTimer) clearInterval(consentPollTimer); });
// Re-poll once the inbox list loads/changes (mount often runs before Vuex is
// populated, so the first poll finds no vaults).
watch(() => allInboxes.value.length, () => pollConsentRequests());
const allAgents  = computed(() => store.getters['agents/getAgents']   ?? []);
// Assignable agents only — admins always have access to every account, so they
// are not listed in assignment pickers (inbox members / chat assignment).
const assignableAgents = computed(() => allAgents.value.filter(a => a.role !== 'administrator'));
// Sorted alphabetically by title — drives both the sidebar LABELS list and the
// labels-overview, so labels read the same everywhere regardless of insert order.
const allLabels  = computed(() =>
  [...(store.getters['labels/getLabels'] ?? [])]
    .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }))
);

// ---- Current user (drives the topbar right side) ---------------------------
const currentUser = computed(() => store.getters.getCurrentUser ?? {});
const userInitials = computed(() => {
  const n = (currentUser.value.name || '').trim();
  if (!n) return '?';
  const p = n.split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '?';
});
const userIsOnline = computed(() => {
  const status = currentUser.value.availability_status;
  return !status || status === 'online';
});

// ---- Topbar user dropdown --------------------------------------------------
const userMenuOpen = ref(false);
const toggleUserMenu = e => {
  e.stopPropagation();
  userMenuOpen.value = !userMenuOpen.value;
};
const closeUserMenu = () => { userMenuOpen.value = false; };

// Navigate to the in-Fangate My Account page. Used by the top-bar user-menu
// dropdown. We close the menu first to avoid relying on the document
// click-listener (which can race with handleSelectView in some browsers),
// then trigger the view switch inside a nextTick so the menu unmount
// doesn't interrupt the listView reactivity.
const goToMyAccount = () => {
  userMenuOpen.value = false;
  nextTick(() => handleSelectView('settings-account'));
};

// Logout via Chatwoot's existing auth endpoint
const logout = async () => {
  userMenuOpen.value = false;
  try {
    // Lazy import to avoid pulling auth API into the initial fangate bundle
    const { default: authAPI } = await import('dashboard/api/auth');
    await authAPI.logout();
  } catch (_) {
    // ignore — we still clear local state and redirect
  }
  try { store.dispatch('logout'); } catch (_) {}
  // Send user back to the sign-in page (Chatwoot's default after logout)
  window.location.assign('/');
};

// ---- Shared account-agent picker (opened from Sidebar + ListPane) -----------
const agentPicker  = reactive({ show: false, x: 0, y: 0, inbox: null });
const inboxAgentMap = reactive({});   // { [inboxId]: agentId[] }

const openAgentPicker = ({ event, inbox }) => {
  // Only admins can (re)assign agents — agents see nothing (prototype parity)
  if (!isAdmin.value) return;
  agentPicker.inbox = inbox;
  agentPicker.show  = true;
  const vw = window.innerWidth, vh = window.innerHeight;
  agentPicker.x = Math.min(event.clientX, vw - 224);
  agentPicker.y = Math.min(event.clientY, vh - 260);
};
const isAgentAssigned = (inboxId, agentId) =>
  !!(inboxId && (inboxAgentMap[inboxId] || []).includes(agentId));

// Hydrate inbox->agents from the server (InboxMembers) once inboxes are loaded.
// Without this the map was empty on load, so isAgentAssigned AND the new
// account-by-agent filter were wrong until the admin manually toggled.
const hydrateInboxAgentMap = async () => {
  await Promise.all((allInboxes.value || []).map(async (ix) => {
    try {
      const resp = await InboxMembersAPI.show(ix.id);
      const members = resp?.data?.payload || resp?.data || [];
      inboxAgentMap[ix.id] = members.map(m => Number(m.id));
    } catch (_) { /* inbox may have no members yet */ }
  }));
};

// Toggle an agent on an inbox AND persist it. Previously this only mutated
// local state — the right-click picker silently lost assignments on reload.
// Now it PATCHes InboxMembers with the full new agent list (optimistic local
// update first, rollback-toast on failure).
const toggleInboxAgent = (inboxId, agentId) => {
  if (!inboxId) return;
  let nextList;
  if (agentId === null) {
    nextList = [];                       // "— Unassign" clears all members
  } else {
    const list = inboxAgentMap[inboxId] || [];
    nextList = list.includes(agentId)
      ? list.filter(id => id !== agentId)
      : [...list, agentId];
  }
  const prev = inboxAgentMap[inboxId];
  inboxAgentMap[inboxId] = nextList;     // optimistic
  InboxMembersAPI.update({ inboxId: Number(inboxId), agentList: nextList })
    .catch(() => {
      inboxAgentMap[inboxId] = prev;     // rollback
      useAlert('Could not save the agent assignment — please retry.');
    });
};

// ---- Selected inbox / conversation ------------------------------------------
const selectedInboxId        = ref(null);
const selectedConversationId = ref(null);
const selectedConversation   = ref(null);   // full conv object from ListPane
const listMode               = ref('all');   // 'all' | 'unread'
const listView               = ref('chats');
const selectedLabel          = ref(null);    // active label title (cross-account label view)

// Note: we used to force-refresh unread counts on every inbox pick — that
// added a REST roundtrip the user explicitly didn't want ("nur account
// anklicken ist keine aktualisierung"). Counts already refresh on mount,
// on focus, every 30s, AND on every relevant Vuex mutation (ADD_MESSAGE,
// UPDATE_MESSAGE_UNREAD_COUNT, etc.), so the click-trigger was redundant.

// Dynamic browser-tab title: "(N) Creator-Chat" when there are unread
// chats, plain "Creator-Chat" when zero. N counts inboxes-with-unread,
// matching the sidebar pill so the number the user sees in the tab matches
// what they see in the sidebar. Updates instantly via the Vuex-derived
// unreadByInbox computed.
const totalUnreadChats = computed(() => {
  let sum = 0;
  const byInbox = unreadByInbox.value || {};
  for (const k of Object.keys(byInbox)) sum += byInbox[k] || 0;
  return sum;
});
watch(totalUnreadChats, n => {
  const base = 'Creator-Chat';
  document.title = n > 0 ? `(${n}) ${base}` : base;
}, { immediate: true });
// listView values:
//   'chats' | 'channels' | 'agents'        — content depends on selected inbox
//   'fangate-vault'                        — fullscreen Fangate vault view
//   'settings-inboxes' | 'settings-bots'   — Fangate-inline settings panels
//   'settings-account'                        (Col 3 swaps to FangateSettings)
const isSettingsView = computed(() => listView.value.startsWith('settings-'));
const isVaultView    = computed(() => listView.value === 'fangate-vault');
const isAgentsView   = computed(() => listView.value === 'agents');
// Reports sub-views. 'reports-accounts' = Account performance — keeps Col 2 (its
// connected-account list, like Chats) + shows the analytics for the picked
// account in Col 3. 'reports-agents'/'reports-custom' are full-space (Col 3 only).
const isReportsAccounts = computed(() => listView.value === 'reports-accounts');
const isReportsAgents   = computed(() => listView.value === 'reports-agents');
const isReportsCustom   = computed(() => listView.value === 'reports-custom');
// Views that take over the chat list + chat area (replacing them with their
// own fullscreen content). reports-accounts is NOT here — it keeps its Col 2 list.
const isFullspaceView = computed(() => isSettingsView.value || isVaultView.value || isAgentsView.value || isReportsAgents.value || isReportsCustom.value);
// On mobile, exactly ONE pane shows. The "main" pane (chat or a fullspace view)
// takes over when a conversation is open OR a fullspace view is active; otherwise
// the list pane is shown. Desktop ignores this (CSS only reacts under .is-mobile).
const mobileMain = computed(() =>
  isMobile.value && (
    selectedConversationId.value != null ||
    isFullspaceView.value ||
    // Reports → Account performance keeps Col 2 (the account list) on desktop, so
    // it's deliberately NOT a fullspace view. On mobile, picking an account must
    // still drill into the main pane (its analytics) — else the list pane stays up.
    (isReportsAccounts.value && selectedFangateInbox.value != null)
  )
);
// Mobile-only: the Fangate panel is hidden until the chat-header FG pill opens
// it as a right slide-over (desktop keeps the always-on Col 4 panel).
const mobileFangateOpen = ref(false);
// Whitelist for the Fangate panel (Col 4): it represents per-chat content,
// so it only belongs on Chats and Channels views. Everything else (agents,
// fangate-vault, reports, settings, …) hides it.
const showFangatePanel = computed(
  () => listView.value === 'chats' || listView.value === 'channels'
);

// Selected Fangate account (= inbox object) for the vault view.
const selectedFangateInbox = ref(null);

// ---- Role — the REAL Chatwoot account role (#110). Drives the agent-picker
// + the Agents page here, and the Reports/Settings sections in the sidebar.
// Replaced the old demo Admin/Agent toggle that shipped visible in production.
const { isAdmin } = useAdmin();

// Views only an account-administrator may open. Agents are redirected to 'chats'
// if they reach one via stale URL / localStorage restore / a deep link — the
// sidebar + rail already hide the entry points, this is defense-in-depth so the
// route can't be forced. (Account performance / fangate-vault are NOT here:
// agents see those, scoped to their assigned accounts by the ACL + server gate.
// settings-account / settings-help stay open — agents keep their own profile.)
const ADMIN_ONLY_VIEWS = [
  'agents', 'reports-custom', 'reports-agents', 'settings-inboxes', 'settings-bots',
];
const gateView = view => (!isAdmin.value && ADMIN_ONLY_VIEWS.includes(view) ? 'chats' : view);

// ---- First-run onboarding overlay (the welcome tour) -----------------------
// confirmation/Index.vue sets `cc_show_onboarding` when the user clicks
// "Access Dashboard" right after verifying. We show the tour ONCE as a
// transparent iframe overlay over the (loaded) dashboard; the iframe posts
// 'cc-onboarding-done' back on Skip/Finish so we remove it. `cc_onboarded` (set
// inside the tour) guards against re-showing.
const showOnboarding = ref(false);
const onOnboardingMessage = e => {
  if (e?.data === 'cc-onboarding-done') showOnboarding.value = false;
};
onMounted(() => {
  try {
    if (localStorage.getItem('cc_show_onboarding') === '1'
        && localStorage.getItem('cc_onboarded') !== '1') {
      showOnboarding.value = true;
    }
    localStorage.removeItem('cc_show_onboarding');
  } catch (e) { /* storage unavailable */ }
  window.addEventListener('message', onOnboardingMessage);
});
onUnmounted(() => window.removeEventListener('message', onOnboardingMessage));

// ---- Resizable columns — Sidebar↔List & List↔Chat dividers ----------------
// Two drag-to-resize handles redistribute width between the columns. The chat
// area (Col 3) is flex-1 and absorbs whatever the resized columns give up or
// take. Bounds keep every button + label visible at both extremes. The chosen
// widths persist in localStorage per browser, so they stick across reloads.
//
// COLUMN spec — { width ref, min, max, default, storageKey }:
const COLS = {
  sidebar: { default: 260, min: 170, max: 320, key: 'cc_layout_sidebar_w:v1' },
  list:    { default: 270, min: 220, max: 380, key: 'cc_layout_list_w:v1'    },
};

const clampW = (cfg, w) => Math.max(cfg.min, Math.min(cfg.max, w));
const loadW  = cfg => {
  try {
    const raw = parseInt(localStorage.getItem(cfg.key) || '', 10);
    return Number.isFinite(raw) ? clampW(cfg, raw) : cfg.default;
  } catch (_) { return cfg.default; }
};

const sidebarWidth = ref(loadW(COLS.sidebar));
const listWidth    = ref(loadW(COLS.list));

// Drag state — single object, since only one divider can be dragged at a time.
// `target` selects which width ref the move handler mutates; `startX`/`startW`
// snapshot the pointer + width at mousedown so subsequent moves are computed
// from the snapshot (no drift on fast pointer moves).
const dragging = reactive({ active: false, target: null, startX: 0, startW: 0 });

const onColDragMove = e => {
  if (!dragging.active) return;
  const delta = e.clientX - dragging.startX;
  const cfg   = COLS[dragging.target];
  const next  = clampW(cfg, dragging.startW + delta);
  if (dragging.target === 'sidebar') sidebarWidth.value = next;
  else                                listWidth.value    = next;
};

const onColDragEnd = () => {
  if (!dragging.active) return;
  const cfg = COLS[dragging.target];
  const w   = dragging.target === 'sidebar' ? sidebarWidth.value : listWidth.value;
  dragging.active = false;
  dragging.target = null;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  window.removeEventListener('mousemove', onColDragMove);
  window.removeEventListener('mouseup',   onColDragEnd);
  try { localStorage.setItem(cfg.key, String(w)); }
  catch (_) { /* storage unavailable — width still holds for the session */ }
};

const startColDrag = (target, e) => {
  dragging.active = true;
  dragging.target = target;
  dragging.startX = e.clientX;
  dragging.startW = target === 'sidebar' ? sidebarWidth.value : listWidth.value;
  // Body-level cursor + select-suppression so the cursor stays as col-resize
  // even when the pointer briefly leaves the 6px divider, and the drag doesn't
  // accidentally start a text selection in the panes.
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
  window.addEventListener('mousemove', onColDragMove);
  window.addEventListener('mouseup',   onColDragEnd);
  e.preventDefault();
};

// Double-click any divider → reset that column to its default (escape hatch
// if the user drags themselves into an awkward position).
const resetColWidth = target => {
  const cfg = COLS[target];
  if (target === 'sidebar') sidebarWidth.value = cfg.default;
  else                       listWidth.value    = cfg.default;
  try { localStorage.setItem(cfg.key, String(cfg.default)); }
  catch (_) { /* no-op */ }
};

// Clear in-flight listeners on unmount (safety: dragging across an unmount is
// rare but cheap to guard).
onUnmounted(() => {
  window.removeEventListener('mousemove', onColDragMove);
  window.removeEventListener('mouseup',   onColDragEnd);
});

// Clear the currently-open conversation and its Vuex echo. Used by every
// navigation transition that swaps the list pane to a different inbox or
// view so the chat area on the right doesn't keep rendering the previous
// conversation's messages (the "selected channel has no chats but I still
// see the old Sven thread" bug).
const clearSelectedConversation = () => {
  if (selectedConversationId.value === null && selectedConversation.value === null) return;
  selectedConversation.value   = null;
  selectedConversationId.value = null;
  // Tell Chatwoot's Vuex store to drop its idea of the active chat too —
  // otherwise the right-pane components still pull stale data from
  // getSelectedChat.
  store.dispatch('clearSelectedState');
};

const handleSelectInbox = id => {
  // Keep the current list view if the user is already browsing 'chats' or
  // 'channels' (clicking an account from the channels-overview should stay
  // in channels view, NOT bounce them into chats). Only fall back to 'chats'
  // when coming from a non-list view (settings, agents, fangate, etc.).
  if (!['chats', 'channels'].includes(listView.value)) {
    listView.value = 'chats';
  }
  selectedLabel.value = null;   // leaving any cross-account label view
  // Switching to a different inbox: the previously-open conversation almost
  // certainly belongs to the old inbox — clear it.
  if (Number(id) !== Number(selectedInboxId.value)) clearSelectedConversation();
  selectedInboxId.value = id;
};
// ListPane now emits the full conversation object so we don't need Vuex lookups
const handleSelectConversation = conv => {
  selectedConversation.value   = conv;
  selectedConversationId.value = conv?.id ?? null;

  // Sync the selection into Vuex so Chatwoot's <MessageList> + <MessageMeta>
  // (which use the `useInbox` composable reading `getSelectedChat`) can
  // determine the channel type. Without this, `selectedChatId` stays null,
  // `isATelegramChannel` is false, and every outgoing message falls through
  // to the PROGRESS status → clock icon ("sending") instead of a check.
  if (conv) {
    // Highlight the chat's account in the sidebar — important for the cross-
    // account LABEL view (and search), where no inbox was previously selected.
    // The list pane stays on its current view: FangateListPane.currentLayer
    // ignores selectedInboxId while view === 'label', so this doesn't slide the
    // label list away to the per-inbox chat list.
    if (conv.inbox_id != null) selectedInboxId.value = conv.inbox_id;
    store.commit('ADD_CONVERSATION', conv);          // ensures it's in allConversations
    store.commit('SET_CURRENT_CHAT_WINDOW', conv);   // sets selectedChatId

    // Mark conversation as read on open. Chatwoot's own ChatList does this
    // implicitly; our custom Fangate list never did, leaving per-conv pills
    // and the unread row emphasis stuck forever.
    //
    // We bypass Chatwoot's markMessagesRead action because it intentionally
    // delays the unread→0 commit by 4 s — fine for Chatwoot's UX, jarring
    // here. Instead: commit the mutation immediately (instant feedback),
    // then fire-and-forget the REST call so the backend agrees.
    if ((conv.unread_count || 0) > 0) {
      const nowSec = Math.floor(Date.now() / 1000);
      store.commit('UPDATE_MESSAGE_UNREAD_COUNT', {
        id: conv.id,
        lastSeen: nowSec,
        unreadCount: 0,
      });
      ConversationAPI.markMessageRead({ id: conv.id }).catch(() => {});
    }
  }
};
const handleBack = () => {
  clearSelectedConversation();
  selectedInboxId.value = null;
};
// Mobile back from an open chat → return to the chat LIST of the same account
// (keep selectedInboxId so the list shows that inbox's chats, not the accounts
// layer). The list pane's own back button still steps chats → accounts.
const mobileBack = () => { mobileFangateOpen.value = false; clearSelectedConversation(); };

// --- Browser / hardware / swipe BACK → in-app "back" (URL-driven) ------------
// The Fangate drill-down (accounts→chats→chat→panel, settings sections, vault,
// agents, label view) lives in component state, not the router. So that BACK
// steps ONE level up IN-APP instead of leaving the page — on iOS *and* Android
// — every navigation writes a DISTINCT URL into history; BACK pops it and we
// restore the matching state. Implementation lives with the URL-state code
// below (writeUrlFromState / applyNavFromUrl / onFangatePopState) so it can
// share the param helpers. The previous single same-URL sentinel worked on
// Android but was skipped by iOS Chrome's snapshot-based back navigation.
// Channels nav: account clicked → switch list pane into channels view.
// Always drop any selected conversation here — the channels view shows a
// different list (channels/groups, not personal chats) and rendering the
// old chat alongside an empty channels list is what triggered the
// "no channel selected but stale Sven thread" bug.
const handleSelectChannelAccount = id => {
  listView.value         = 'channels';
  clearSelectedConversation();
  selectedInboxId.value  = id;
};
// CHATS sidebar nav: account clicked → mirror of handleSelectChannelAccount.
// Explicit force-into-'chats' (separate from handleSelectInbox which is
// view-preserving for listpane account-card clicks). Without this, clicking
// a CHATS-section row while the listpane is in channels view leaves the
// view in 'channels' and the user ends up looking at channels of their
// just-picked "chats" account.
const handleSelectChatAccount = id => {
  listView.value         = 'chats';
  selectedLabel.value    = null;
  clearSelectedConversation();
  selectedInboxId.value  = id;
};
// Global search (sidebar) → open a conversation hit IN OUR SHELL. Switch to the
// hit's inbox (so the list pane shows the right context), then open the
// conversation (handleSelectConversation adds it to Vuex + sets the chat window;
// FangateChatArea fetches its messages by display_id).
const handleSearchConversation = conv => {
  if (!conv?.id) return;
  if (conv.inbox_id) handleSelectInbox(conv.inbox_id);
  handleSelectConversation(conv);
  // A message hit carries the specific message id → tell ChatArea to scroll to
  // it (a plain conversation hit has no messageId → no jump, opens at bottom).
  if (conv.messageId) {
    jumpMessageId.value = conv.messageId;
    jumpNonce.value += 1;
  }
};
// Sidebar LABELS section → cross-account view of all readable chats carrying
// this label. Clears inbox/conversation context; the list pane (view='label')
// fetches the conversations by label server-side (ACL-respected) and renders them.
const handleSelectLabel = title => {
  if (!title) return;
  listView.value        = 'label';
  selectedLabel.value   = title;
  selectedInboxId.value = null;
  clearSelectedConversation();
};
// Top-level view switcher (currently used for Agents nav)
const handleSelectView = view => {
  view = gateView(view);
  listView.value        = view;
  selectedLabel.value   = null;
  clearSelectedConversation();
  selectedInboxId.value = null;
  // Reports landing views start with no account picked (the list shows in Col 2;
  // Col 3 stays on its "pick an account" state until the user clicks one).
  if (view.startsWith('reports-')) selectedFangateInbox.value = null;
};
// Fangate sidebar emits this when the user picks an account from the dropdown.
// Switch into the fullscreen vault view bound to that inbox.
const handleSelectFangateAccount = acc => {
  if (!acc?.id) return;
  selectedFangateInbox.value = acc;
  listView.value = 'fangate-vault';
  selectedLabel.value = null;
  // Drop conversation context — the vault view doesn't use it.
  selectedInboxId.value = null;
};
// Reports → "Transactions & Live analytics" for ONE account. Same selected-inbox
// state as the vault, but the full-space view is the analytics panel.
const handleSelectReportsAccount = acc => {
  if (!acc?.id) return;
  selectedFangateInbox.value = acc;
  listView.value = 'reports-accounts';
  selectedLabel.value = null;
  selectedInboxId.value = null;
};
// Vault "Back" button → go back to chats. Keep the selected account in
// state so reopening the vault doesn't lose context.
const handleVaultClose = () => {
  listView.value = 'chats';
  // The user may have just accepted/declined a tag in Collaborations — refresh
  // the consent indicator so the dot reflects reality promptly.
  pollConsentRequests();
};

// "+ New" button in the chat list pane header → open the contact picker
// modal (#143). Modal lists every contact that has at least one
// conversation on this inbox (from Vuex), with search by name/@username.
// Telegram bots can't cold-DM strangers, so we surface that constraint in
// the modal itself when the inbox has no existing contacts.
const newChatModalOpen = ref(false);
const newChatInboxId   = ref(null);
const handleNewChat = inboxId => {
  newChatInboxId.value   = inboxId;
  newChatModalOpen.value = true;
};
// Modal emits 'select-conversation' with the conversation's display_id when the
// user picks a contact whose conversation already exists. handleSelectConversation
// needs the full conversation OBJECT (it commits ADD_CONVERSATION + reads
// inbox_id for channel-type detection) — passing the bare number left
// selectedConversationId undefined and never opened the chat (#audit bug 1).
// Resolve the object from Vuex before handing off.
const handleNewChatPicked = conversationId => {
  const conv =
    (store.getters.getAllConversations || []).find(
      c => Number(c.id) === Number(conversationId)
    ) || { id: conversationId };
  handleSelectConversation(conv);
};

// ---- URL state persistence + browser/hardware BACK navigation --------------
// Every nav state change writes a DISTINCT URL into history via the History API
// (NOT router.push — an earlier router-based version collapsed the flex layout).
// The browser / hardware / swipe BACK then pops to the previous URL and
// onFangatePopState restores the matching state → one level of in-app "back",
// on iOS *and* Android (the old same-URL sentinel was skipped by iOS Chrome's
// snapshot-based back). This is a MOBILE affordance — gated on isMobile so the
// desktop back button keeps its normal behaviour. Param names are generic so
// the brand never appears in the URL (listView 'fangate-vault' → v=vault).
// localStorage is still written on every change for F5 survival.
const LS_KEY = 'fangate_layout_state';

const NAV_KEYS = ['v', 'a', 'c', 'va', 'panel', 'lbl'];
const viewToUrl = v => (v === 'fangate-vault' ? 'vault' : v);
const urlToView = v => (v === 'vault' ? 'fangate-vault' : v);

// Current nav state → query params, preserving any unrelated params already on
// the URL. 'chats' is the default view → omitted to keep the URL clean.
const buildNavParams = () => {
  const params = new URLSearchParams(window.location.search);
  NAV_KEYS.forEach(k => params.delete(k));
  const v = listView.value;
  if (v && v !== 'chats') params.set('v', viewToUrl(v));
  if (selectedInboxId.value != null) params.set('a', String(selectedInboxId.value));
  if (selectedConversationId.value != null) params.set('c', String(selectedConversationId.value));
  if (selectedFangateInbox.value?.id != null) params.set('va', String(selectedFangateInbox.value.id));
  if (mobileFangateOpen.value) params.set('panel', '1');
  if (v === 'label' && selectedLabel.value) params.set('lbl', String(selectedLabel.value));
  return params;
};
const navUrl = () => {
  const qs = buildNavParams().toString();
  return window.location.pathname + (qs ? `?${qs}` : '');
};

const writeUrlFromState = () => {
  if (restoring) return;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({
      view:         listView.value,
      mode:         listMode.value,
      inboxId:      selectedInboxId.value,
      convId:       selectedConversationId.value,
      vaultInboxId: selectedFangateInbox.value?.id ?? null,
    }));
  } catch (_) { /* ignore quota / privacy mode */ }
  // Distinct history entry so BACK steps in-app (mobile only). The !== guard
  // makes this a no-op when the URL already matches — e.g. a watcher firing
  // right after a restore — which keeps us from stacking duplicate entries.
  if (!isMobile.value) return;
  try {
    const next = navUrl();
    if (next !== window.location.pathname + window.location.search) {
      window.history.pushState({ fgNav: true }, '', next);
    }
  } catch (_) { /* ignore */ }
};

watch(
  [listView, listMode, selectedInboxId, selectedConversationId, selectedFangateInbox, mobileFangateOpen],
  writeUrlFromState
);

// Restore nav state from whatever the current URL encodes (popstate + deep-link
// + F5). Sets refs directly; `restoring` + the URL-match guard above keep the
// watcher from pushing a duplicate entry while we read.
const applyNavFromUrl = async () => {
  const params = new URLSearchParams(window.location.search);
  const v   = gateView(params.has('v') ? urlToView(params.get('v')) : 'chats');
  const a   = params.get('a');
  const c   = params.get('c');
  const va  = params.get('va');
  const lbl = params.get('lbl');

  listView.value        = v;
  selectedLabel.value   = v === 'label' ? (lbl || null) : null;
  selectedInboxId.value = a != null ? Number(a) : null;

  // Fullscreen vault selection — resolve the inbox object; drop to chats if it
  // is no longer readable (deleted / permissions changed).
  const vaId = va != null ? Number(va) : null;
  if (vaId != null) {
    const inbox = (allInboxes.value || []).find(i => Number(i.id) === vaId);
    if (inbox) selectedFangateInbox.value = inbox;
    else if (v === 'fangate-vault') listView.value = 'chats';
  } else {
    selectedFangateInbox.value = null;
  }

  mobileFangateOpen.value = params.get('panel') === '1';

  const cId = c != null ? Number(c) : null;
  if (cId != null && Number.isFinite(cId)) {
    if (Number(selectedConversationId.value) !== cId) {
      selectedConversationId.value = cId;
      try {
        const resp = await ConversationAPI.show(cId);
        const conv = resp?.data?.data ?? resp?.data ?? null;
        if (conv && Number(conv.id) === cId) {
          selectedConversation.value = conv;
          store.commit('ADD_CONVERSATION', conv);
          store.commit('SET_CURRENT_CHAT_WINDOW', conv);
        }
      } catch (_) {
        // Conv was deleted or 404'd — clear the stale id.
        selectedConversationId.value = null;
      }
    }
  } else {
    clearSelectedConversation();
  }
};

// Browser / hardware / swipe BACK (mobile) → restore the state of the URL we
// popped to = one level of in-app "back". At the base URL there is nothing left
// to restore, so a further back leaves the app normally (never trapped).
const onFangatePopState = async () => {
  if (!isMobile.value) return;
  restoring = true;
  try { await applyNavFromUrl(); }
  finally { nextTick(() => { restoring = false; }); }
};
onMounted(() => window.addEventListener('popstate', onFangatePopState));
onUnmounted(() => window.removeEventListener('popstate', onFangatePopState));

const restoreFromUrl = async () => {
  // The all/unread filter is not a nav level → always taken from localStorage.
  let saved = null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    saved = raw ? JSON.parse(raw) : null;
  } catch (_) { /* ignore */ }
  if (saved?.mode) listMode.value = String(saved.mode);

  // Deep-link / F5: nav params already on the URL win (works on any device).
  const params = new URLSearchParams(window.location.search);
  if (NAV_KEYS.some(k => params.has(k))) {
    await applyNavFromUrl();
    return;
  }

  // Otherwise restore the last session from localStorage, then (mobile only)
  // reflect it into the URL with replaceState — no extra entry — so the first
  // BACK has a base state to pop to.
  if (!saved) return;
  if (saved.view)    listView.value = gateView(String(saved.view));
  if (saved.inboxId) selectedInboxId.value = Number(saved.inboxId);

  if (saved.vaultInboxId) {
    const id = Number(saved.vaultInboxId);
    const inbox = (allInboxes.value || []).find(i => Number(i.id) === id);
    if (inbox) selectedFangateInbox.value = inbox;
    else if (listView.value === 'fangate-vault') listView.value = 'chats';
  }

  if (saved.convId) {
    const id = Number(saved.convId);
    if (Number.isFinite(id)) {
      selectedConversationId.value = id;
      try {
        const resp = await ConversationAPI.show(id);
        const conv = resp?.data?.data ?? resp?.data ?? null;
        if (conv && Number(conv.id) === id) {
          selectedConversation.value = conv;
          store.commit('ADD_CONVERSATION', conv);
          store.commit('SET_CURRENT_CHAT_WINDOW', conv);
        }
      } catch (_) {
        selectedConversationId.value = null;
      }
    }
  }

  if (isMobile.value) {
    try {
      const qs = buildNavParams().toString();
      if (qs) window.history.replaceState({ fgNav: true }, '', `${window.location.pathname}?${qs}`);
    } catch (_) { /* ignore */ }
  }
};

// ---- Right-panel collapse state ---------------------------------------------
const panelCollapsed = ref(false);
const togglePanel = () => {
  panelCollapsed.value = !panelCollapsed.value;
};

// ---- Fangate panel handlers -----------------------------------------------
// "Copy to chat" from FangatePanel → bubble the URL into the active conversation.
// The chat compose area listens via the `external-text-insert` channel — see
// FangateChatArea for the receiving end.
const externalChatText = ref('');     // last text injected from Fangate panel
const externalNonce    = ref(0);      // bump to force ChatArea to re-pick same text

const handleInsertIntoChat = text => {
  if (!text) return;
  externalChatText.value = text;
  externalNonce.value += 1;
  mobileFangateOpen.value = false;   // close the mobile slide-over after copy-to-chat
};

// Jump-to-message signal for FangateChatArea. A global-search message hit sets
// the target id + bumps the nonce; the nonce re-triggers a scroll even when the
// hit is in the already-open conversation. See handleSearchConversation.
const jumpMessageId = ref(null);
const jumpNonce     = ref(0);
// Resolve the currently-selected inbox object (passed as activeInbox to the
// FangatePanel — drives the "Vault from {Creator}" pill label)
const selectedInbox = computed(() =>
  allInboxes.value.find(i => i.id === selectedInboxId.value) || null
);
// The account shown in the mobile rail's account pill: the selected inbox, or
// the first account as a sensible default when nothing is selected yet (e.g. on
// the accounts overview). Tapping the pill opens the overview to switch.
const activeRailInbox = computed(() => selectedInbox.value || allInboxes.value?.[0] || null);

// Empty-state copy shown (mobile) when NO chat account is connected yet and the
// user taps a section that needs one (Chats / Channels / Fangate vault).
const noAcctTitle = computed(() => {
  if (listView.value === 'channels') return 'No channel or group selected';
  if (listView.value === 'fangate-vault') return 'No Fangate vault yet';
  return 'No conversation selected';
});
const noAcctText = computed(() =>
  listView.value === 'fangate-vault'
    ? 'Add an account and connect it to your Fangate vault to get started.'
    : 'Add a chat account to get started.'
);
// How many OTHER (non-active) accounts have ≥1 unread message — a badge on the
// rail account pill nudges the agent to switch. Counts ACCOUNTS, not messages
// (1 if one other account has unread, whether it's 1 or 10; 2 if two do) — same
// logic as the desktop tool.
const otherAccountsWithUnread = computed(() => {
  const activeId = activeRailInbox.value?.id;
  const dm = unreadByInbox.value || {};
  const ch = unreadByInboxChannels.value || {};
  let count = 0;
  for (const ib of allInboxes.value || []) {
    if (ib.id === activeId) continue;
    if ((dm[ib.id] || 0) + (ch[ib.id] || 0) > 0) count += 1;
  }
  return count;
});

</script>

<template>
  <!-- .fangate-shell activates ALL scoped design tokens from _fangate-theme.scss -->
  <div class="fangate-shell fangate-root" :class="{ 'is-mobile': isMobile, 'm-main': mobileMain, 'm-fangate-open': mobileFangateOpen }">

    <!-- One-time "what's new" pop-up (shown on login, then dismissed) -->
    <AnnouncementPopup />

    <!-- ===== Diagnostic error overlay ===== -->
    <div v-if="errorInfo" style="position:fixed;top:0;left:0;right:0;z-index:99999;
                                  background:#fee;color:#900;border-bottom:2px solid #c00;
                                  padding:10px 16px;font-family:monospace;font-size:12px;
                                  white-space:pre-wrap;max-height:240px;overflow:auto;">
      <strong>Fangate caught error:</strong>
      {{ errorInfo.message }}<br>
      <small style="opacity:0.7;">{{ errorInfo.info }}</small>
      <pre style="margin:6px 0 0;font-size:11px;opacity:0.7;">{{ errorInfo.stack }}</pre>
      <button @click="errorInfo = null" style="margin-top:6px;padding:2px 8px;">dismiss</button>
    </div>


    <!-- ===================================================================
         TOPBAR (72 px, dark)
         =================================================================== -->
    <header v-if="!isMobile" class="fangate-topbar">
      <!-- Brand left — exact prototype mark + wordmark. NOT a link inside the
           tool: the logo must never navigate away from the app (the
           creator-chat.com link belongs ONLY to the public FAQ page). -->
      <div class="brand-top dark">
        <span class="brand-top-mark" title="Creator-Chat">
          <!-- Header chat-bubbles mark (teal + gold) — inline SVG matches
               the "Header _ uppercase gold _" prototype 1:1. Transparent
               background, no frame — sits cleanly on whatever the topbar
               color happens to be. -->
          <svg width="32" height="32" viewBox="0 0 96 92" xmlns="http://www.w3.org/2000/svg"
               aria-hidden="true" style="display:block">
            <defs>
              <linearGradient id="cc-goldgrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#d8b86a"/>
                <stop offset="0.5" stop-color="#a8842f"/>
                <stop offset="1" stop-color="#7a5a14"/>
              </linearGradient>
              <clipPath id="cc-clipover">
                <rect x="38" y="44" width="20" height="22"/>
              </clipPath>
            </defs>
            <!-- Teal bubble (back) -->
            <path d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
                  transform="translate(7,11)" fill="none" stroke="#0d9488"
                  stroke-width="7" stroke-linejoin="round"/>
            <g transform="translate(7,11)" fill="#0d9488">
              <circle cx="15" cy="18" r="2.4"/>
              <circle cx="24" cy="18" r="2.4"/>
              <circle cx="33" cy="18" r="2.4"/>
            </g>
            <!-- Gold bubble (front, overlapping) -->
            <path d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 32 36 L 37 47 L 20 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
                  transform="translate(41,30)" fill="none" stroke="url(#cc-goldgrad)"
                  stroke-width="7" stroke-linejoin="round"/>
            <g transform="translate(41,30)" fill="#a8842f">
              <circle cx="15" cy="18" r="2.4"/>
              <circle cx="24" cy="18" r="2.4"/>
              <circle cx="33" cy="18" r="2.4"/>
            </g>
            <!-- Re-stroke the teal outline in the overlap region so the
                 teal bubble visually crosses on top of the gold tail -->
            <g clip-path="url(#cc-clipover)">
              <path d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
                    transform="translate(7,11)" fill="none" stroke="#0d9488"
                    stroke-width="7" stroke-linejoin="round"/>
            </g>
          </svg>
        </span>
        <div class="brand-top-tagline-wrap">
          <!-- Wordmark per "Header _ uppercase gold _" prototype:
               CREATOR (Fraunces 400 cream) — (gold-light) CHAT (Fraunces 300 cream) .com (gold).
               All uppercase, letter-spacing 1.26px. -->
          <span class="brand-top-name">
            <span class="creator">Creator</span><span class="accent">-</span><span class="chat">Chat</span><span class="brand-top-tld">.com</span>
          </span>
          <div class="brand-top-divider"></div>
          <!-- Tagline: gold middle-dot bumped one size + Inter 600 per prototype. -->
          <span class="brand-top-tagline">Conversations <span class="brand-top-tagline-dot">·</span> Tailored</span>
        </div>
      </div>

      <!-- Center — empty per prototype -->
      <div class="topbar-center"></div>

      <!-- Right — current user (name + role + avatar + dropdown) -->
      <div class="topbar-right">
        <!-- Co-creator consent requests — a SEPARATE icon, kept apart from the
             chat-notification bell. Only shown while requests are pending.
             Click opens a small popover to Accept (fires straight to Fangate)
             or Decline (jumps to Collaborations). -->
        <div v-if="consentPendingCount" class="tb-consent-wrap" v-on-click-outside="closeConsentPopover">
          <button class="tb-consent" type="button"
                  @click.stop="toggleConsentPopover"
                  :title="consentPendingCount + ' co-creator tagging request(s) need your approval'">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <span class="tb-consent-badge">{{ consentBadge }}</span>
          </button>
          <div v-if="consentOpen" class="tb-consent-panel" @click.stop>
            <div class="tb-consent-head">Tagging requests</div>
            <div v-for="(row, i) in consentTagRows" :key="'ctag-' + i" class="tb-consent-item">
              <div class="tb-consent-text">
                <strong>{{ taggerLabel(row.tag) }}</strong> tagged you<span v-if="row.tag.product_id"> in product #{{ row.tag.product_id }}</span>. Please accept or decline, or <a href="#" class="tb-consent-link" @click.prevent="openConsentDetails(row.inbox)">click here</a> to see further details.
              </div>
              <div class="tb-consent-actions">
                <button class="tb-consent-accept" :disabled="respondingTagId === row.tag.id"
                        @click="acceptConsentTag(row.inbox, row.tag)">
                  {{ respondingTagId === row.tag.id ? 'Accepting…' : 'Accept' }}
                </button>
                <button class="tb-consent-decline" @click="declineConsentTag(row.inbox)">Decline</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Notification bell + inline dropdown (last 6 + Clear). v-on-click-
             outside closes the panel when the user clicks anywhere else. -->
        <div class="tb-bell-wrap" v-on-click-outside="closeBell">
          <button class="tb-bell" type="button" @click.stop="openBell" :title="'Notifications' + (unreadCount ? ' (' + unreadCount + ' unread)' : '')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
            </svg>
            <span v-if="unreadBadge" class="tb-bell-badge">{{ unreadBadge }}</span>
          </button>
          <div v-if="bellOpen" class="tb-bell-panel" @click.stop>
            <div class="tb-bell-head">Notifications</div>
            <div v-if="!latestNotifications.length" class="tb-bell-empty">
              No notifications.
            </div>
            <div v-else class="tb-bell-list">
              <div v-for="n in latestNotifications" :key="n.id"
                   class="tb-bell-item" :class="{ 'is-unread': !n.read_at }"
                   role="button" tabindex="0"
                   @click="onClickNotification(n)">
                <div class="tb-bell-item-title">{{ n.push_message_title || n.notification_type }}</div>
                <div class="tb-bell-item-time">{{ formatNotificationTime(n.created_at) }}</div>
              </div>
            </div>
            <button v-if="latestNotifications.length" type="button"
                    class="tb-bell-clear" @click="clearAllNotifications">
              Clear notifications
            </button>
          </div>
        </div>
        <div class="tb-user-text">
          <div class="tb-user-name">{{ currentUser.name || '—' }}</div>
          <div class="tb-user-role">{{ currentUser.role || '' }}</div>
        </div>
        <div class="tb-avatar-wrap" @click.stop="toggleUserMenu">
          <div class="tb-avatar" role="button" tabindex="0" :title="currentUser.name">
            <img v-if="currentUser.avatar_url"
                 :src="currentUser.avatar_url"
                 :alt="currentUser.name" />
            <span v-else>{{ userInitials }}</span>
          </div>
          <span class="tb-status-dot"
                :class="{ 'tb-status-dot--off': !userIsOnline }"></span>
        </div>

        <!-- Dropdown menu -->
        <div v-if="userMenuOpen" class="tb-user-menu" @click.stop>
          <div class="tb-user-menu-head">
            <div class="tb-user-menu-name">{{ currentUser.name || '—' }}</div>
            <div class="tb-user-menu-email">{{ currentUser.email || '' }}</div>
          </div>
          <div class="ctx-sep"></div>
          <button class="ctx-item" @click.stop="goToMyAccount">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            My Account
          </button>
          <div class="ctx-sep"></div>
          <button class="ctx-item danger" @click="logout">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign out
          </button>
        </div>
      </div>
    </header>

    <!-- MOBILE BELL STRIP (Stage 1b) — floats top-right over the content:
         notification bell + tag/consent bell + admin avatar. pointer-events
         are off on the strip itself so it never blocks the header/list beneath;
         only the buttons + open popovers are interactive. Mutually exclusive
         with the desktop topbar (both v-if on isMobile). -->
    <div v-if="isMobile" class="mbell-strip">
      <!-- Tag / consent bell — only while requests pending -->
      <div v-if="consentPendingCount" class="tb-consent-wrap mbell-wrap" v-on-click-outside="closeConsentPopover">
        <button class="mbell-btn" type="button" @click.stop="toggleConsentPopover"
                :title="consentPendingCount + ' co-creator tagging request(s) need your approval'">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"/>
          </svg>
          <span class="mbell-badge teal">{{ consentBadge }}</span>
        </button>
        <div v-if="consentOpen" class="mbell-pop" @click.stop>
          <div class="mbell-pop-head"><span class="mbell-pop-title">Tagging requests</span></div>
          <div class="mbell-pop-body">
            <div v-for="(row, i) in consentTagRows" :key="'mbct-' + i" class="mbell-pop-item">
              <div class="mbell-ic tag">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"/></svg>
              </div>
              <div class="mbell-tx">
                <div class="mbell-it-title"><strong>{{ taggerLabel(row.tag) }}</strong> tagged you<span v-if="row.tag.product_id"> in product #{{ row.tag.product_id }}</span>.</div>
                <div class="mbell-tag-acts">
                  <button class="mbell-acc" :disabled="respondingTagId === row.tag.id" @click="acceptConsentTag(row.inbox, row.tag)">{{ respondingTagId === row.tag.id ? 'Accepting…' : 'Accept' }}</button>
                  <button class="mbell-dec" @click="declineConsentTag(row.inbox)">Decline</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Notification bell -->
      <div class="tb-bell-wrap mbell-wrap" v-on-click-outside="closeBell">
        <button class="mbell-btn" type="button" @click.stop="openBell" :title="'Notifications' + (unreadCount ? ' (' + unreadCount + ' unread)' : '')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          <span v-if="unreadBadge" class="mbell-badge red">{{ unreadBadge }}</span>
        </button>
        <div v-if="bellOpen" class="mbell-pop" @click.stop>
          <div class="mbell-pop-head">
            <span class="mbell-pop-title">Notifications</span>
            <button v-if="latestNotifications.length" class="mbell-pop-clear" @click="clearAllNotifications">Clear all</button>
          </div>
          <div v-if="!latestNotifications.length" class="mbell-empty">No notifications</div>
          <div v-else class="mbell-pop-body">
            <div v-for="n in latestNotifications" :key="'mbn-' + n.id" class="mbell-pop-item"
                 :class="{ 'is-unread': !n.read_at }" role="button" tabindex="0" @click="onClickNotification(n)">
              <div class="mbell-ic msg">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
              <div class="mbell-tx">
                <div class="mbell-it-title">{{ n.push_message_title || n.notification_type }}</div>
                <div class="mbell-it-time">{{ formatNotificationTime(n.created_at) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Admin avatar + user menu -->
      <div class="tb-avatar-wrap mbell-avatar" @click.stop="toggleUserMenu">
        <div class="tb-avatar" role="button" tabindex="0" :title="currentUser.name">
          <img v-if="currentUser.avatar_url" :src="currentUser.avatar_url" :alt="currentUser.name" />
          <span v-else>{{ userInitials }}</span>
        </div>
        <span class="tb-status-dot" :class="{ 'tb-status-dot--off': !userIsOnline }"></span>
      </div>
      <div v-if="userMenuOpen" class="tb-user-menu mbell-user-menu" @click.stop>
        <div class="tb-user-menu-head">
          <div class="tb-user-menu-name">{{ currentUser.name || '—' }}</div>
          <div class="tb-user-menu-email">{{ currentUser.email || '' }}</div>
        </div>
        <div class="ctx-sep"></div>
        <button class="ctx-item" @click.stop="goToMyAccount">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          My Account
        </button>
        <div class="ctx-sep"></div>
        <button class="ctx-item danger" @click="logout">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          Sign out
        </button>
      </div>
    </div>

    <!-- ===================================================================
         BODY — 4 columns in a flex row
         =================================================================== -->
    <div class="fangate-body">

      <!-- ----------------------------------------------------------------
           MOBILE ICON RAIL (Stage 1b) — replaces the dark topbar + 260px nav
           on phones. Slim 52px column: bells (top), light logo, section nav,
           Fangate, Settings, and the admin avatar (bottom). Reuses the exact
           topbar state + handlers. Desktop never renders it (v-if), and the
           topbar is v-if="!isMobile", so the two are never in the DOM at the
           same time → no duplicate click-outside handlers or SVG gradient ids.
           ---------------------------------------------------------------- -->
      <nav v-if="isMobile" class="fangate-mobile-rail" aria-label="Navigation">
        <!-- Light logo (mark only) — the wordmark is dropped on the slim rail -->
        <span class="mrail-logo" title="Creator-Chat">
          <svg width="34" height="34" viewBox="0 0 96 92" xmlns="http://www.w3.org/2000/svg"
               aria-hidden="true" style="display:block">
            <defs>
              <linearGradient id="cc-goldgrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#d8b86a"/>
                <stop offset="0.5" stop-color="#a8842f"/>
                <stop offset="1" stop-color="#7a5a14"/>
              </linearGradient>
              <clipPath id="cc-clipover">
                <rect x="38" y="44" width="20" height="22"/>
              </clipPath>
            </defs>
            <path d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
                  transform="translate(7,11)" fill="none" stroke="#0d9488"
                  stroke-width="7" stroke-linejoin="round"/>
            <g transform="translate(7,11)" fill="#0d9488">
              <circle cx="15" cy="18" r="2.4"/>
              <circle cx="24" cy="18" r="2.4"/>
              <circle cx="33" cy="18" r="2.4"/>
            </g>
            <path d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 32 36 L 37 47 L 20 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
                  transform="translate(41,30)" fill="none" stroke="url(#cc-goldgrad)"
                  stroke-width="7" stroke-linejoin="round"/>
            <g transform="translate(41,30)" fill="#a8842f">
              <circle cx="15" cy="18" r="2.4"/>
              <circle cx="24" cy="18" r="2.4"/>
              <circle cx="33" cy="18" r="2.4"/>
            </g>
            <g clip-path="url(#cc-clipover)">
              <path d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
                    transform="translate(7,11)" fill="none" stroke="#0d9488"
                    stroke-width="7" stroke-linejoin="round"/>
            </g>
          </svg>
        </span>

        <!-- Active Telegram account being operated — avatar + Telegram badge +
             green name. Tap opens the accounts overview to switch accounts. -->
        <button class="mrail-acc" type="button" @click="handleSelectView('chats')"
                :aria-label="'Active account — ' + (activeRailInbox?.name || 'none') + '. Tap to switch accounts'">
          <span class="mrail-acc-av">
            <img v-if="activeRailInbox && activeRailInbox.avatar_url" :src="activeRailInbox.avatar_url" :alt="activeRailInbox.name" />
            <span v-else>{{ (activeRailInbox?.name || '·').charAt(0).toUpperCase() }}</span>
          </span>
          <span class="mrail-acc-badge" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21.5 4.3 2.9 11.4c-1 .4-1 1.3-.1 1.6l4.7 1.5 1.8 5.6c.2.6.6.7 1.1.3l2.6-2.1 4.5 3.3c.5.3 1 .1 1.1-.5l3.3-15.6c.2-.8-.3-1.2-1-.7z"/></svg>
          </span>
          <span v-if="otherAccountsWithUnread" class="mrail-acc-unread"
                :title="otherAccountsWithUnread + ' other account(s) have unread messages'">{{ otherAccountsWithUnread }}</span>
        </button>
        <span class="mrail-acc-name">{{ activeRailInbox?.name || 'Accounts' }}</span>

        <div class="mrail-divider"></div>

        <!-- Section nav — drives the same listView as the desktop sidebar -->
        <button class="mrail-btn" :class="{ 'is-active': listView === 'chats' }"
                title="Chats" @click="handleSelectChatAccount(activeRailInbox?.id ?? null)">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span v-if="activeRailInbox && unreadByInbox[activeRailInbox.id]" class="mrail-dot">{{ unreadByInbox[activeRailInbox.id] }}</span>
        </button>
        <button class="mrail-btn" :class="{ 'is-active': listView === 'channels' }"
                title="Channels &amp; groups" @click="handleSelectChannelAccount(activeRailInbox?.id ?? null)">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 11a9 9 0 0 1 9 9"/><path d="M4 4a16 16 0 0 1 16 16"/><circle cx="5" cy="19" r="1"/>
          </svg>
          <span v-if="activeRailInbox && unreadByInboxChannels[activeRailInbox.id]" class="mrail-dot">{{ unreadByInboxChannels[activeRailInbox.id] }}</span>
        </button>
        <button class="mrail-btn" :class="{ 'is-active': listView === 'labels-overview' }"
                title="Labels" @click="handleSelectView('labels-overview')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
          </svg>
        </button>
        <button v-if="isAdmin" class="mrail-btn" :class="{ 'is-active': listView === 'agents' }"
                title="Agents" @click="handleSelectView('agents')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </button>
        <button class="mrail-btn mrail-fg-btn" :class="{ 'is-active': listView === 'fangate-vault' }"
                title="Fangate vault" @click="activeRailInbox ? handleSelectFangateAccount(activeRailInbox) : handleSelectView('fangate-vault')">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <defs>
              <linearGradient id="fg-rail-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#241689"/><stop offset="0.55" stop-color="#7b1fd6"/><stop offset="1" stop-color="#e0249a"/>
              </linearGradient>
            </defs>
            <circle cx="12" cy="12" r="11.5" style="fill:url(#fg-rail-grad);stroke:none"/>
            <text x="12" y="12.6" text-anchor="middle" dominant-baseline="central" style="fill:#fff;stroke:none" font-family="Inter,system-ui,sans-serif" font-size="9.5" font-weight="800">FG</text>
          </svg>
        </button>
        <button class="mrail-btn" :class="{ 'is-active': listView.startsWith('reports') }"
                title="Transactions & analytics"
                @click="handleSelectView(isAdmin ? 'reports' : 'reports-accounts')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 20h16"/><path d="M7 20v-5"/><path d="M12 20v-9"/><path d="M17 20v-13"/>
          </svg>
        </button>
        <button class="mrail-btn" :class="{ 'is-active': isSettingsView || listView === 'settings' }"
                title="Settings" @click="handleSelectView('settings')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        </button>

      </nav>

      <!-- Mobile: no chat account connected yet → a helpful empty state for the
           account-needing sections (Chats / Channels / Fangate vault), with a
           shortcut to add one. Covers the content area; rail + bells stay usable. -->
      <div v-if="isMobile && !allInboxes.length && ['chats','channels','fangate-vault'].includes(listView)"
           class="m-noacct" role="status">
        <div class="m-noacct-icon" aria-hidden="true">
          <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        </div>
        <h3 class="m-noacct-title">{{ noAcctTitle }}</h3>
        <p class="m-noacct-text">{{ noAcctText }}</p>
        <button class="m-noacct-btn" type="button" @click="handleSelectView('settings-inboxes')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add account
        </button>
      </div>

      <!-- ----------------------------------------------------------------
           COL 1 · Nav Sidebar (260 px, user-resizable via the divider on its
           right edge — see startSidebarDrag)
           ---------------------------------------------------------------- -->
      <nav class="fangate-nav" aria-label="Main navigation"
           :style="{ width: sidebarWidth + 'px', minWidth: sidebarWidth + 'px' }">
        <div class="fangate-nav-scroll">
          <FangateBoundary label="Sidebar">
            <FangateSidebar
              :inboxes="allInboxes"
              :labels="allLabels"
              :unread-by-inbox="unreadByInbox"
              :unread-by-inbox-channels="unreadByInboxChannels"
              :total-by-inbox="totalByInbox"
              :total-by-inbox-channels="totalByInboxChannels"
              :last-activity-by-inbox="lastActivityByInbox"
              :selected-inbox-id="selectedInboxId"
              :selected-fangate-inbox-id="selectedFangateInbox?.id ?? null"
              :view="listView"
              :selected-label="selectedLabel"
              :agents="allAgents"
              :inbox-agent-map="inboxAgentMap"
              @select-inbox="handleSelectInbox"
              @select-chat-account="handleSelectChatAccount"
              @select-channel-account="handleSelectChannelAccount"
              @select-fangate-account="handleSelectFangateAccount"
              @select-reports-account="handleSelectReportsAccount"
              @select-view="handleSelectView"
              @open-agent-picker="openAgentPicker"
              @select-search-conversation="handleSearchConversation"
              @select-label="handleSelectLabel"
            />
          </FangateBoundary>
        </div>
      </nav>

      <!-- Drag-to-resize handle between Sidebar (Col 1) and List Pane (Col 2).
           Thin strip; hover shows the grip; double-click resets to default. -->
      <div class="fangate-col-divider"
           :class="{ 'is-dragging': dragging.active && dragging.target === 'sidebar' }"
           role="separator"
           aria-orientation="vertical"
           :aria-valuenow="sidebarWidth"
           :aria-valuemin="COLS.sidebar.min"
           :aria-valuemax="COLS.sidebar.max"
           title="Drag to resize the sidebar — double-click to reset"
           @mousedown="e => startColDrag('sidebar', e)"
           @dblclick="() => resetColWidth('sidebar')">
        <span class="fangate-col-divider-grip" aria-hidden="true"></span>
      </div>

      <!-- ----------------------------------------------------------------
           COL 2 · List Pane (270 px default, user-resizable via the divider
           on its right edge — see startColDrag('list', ...)). Hidden in
           Settings + Vault views since those take the full right side.
           ---------------------------------------------------------------- -->
      <div v-if="!isFullspaceView"
           class="fangate-list" aria-label="Conversation list"
           :style="{ width: listWidth + 'px', minWidth: listWidth + 'px' }">
        <FangateBoundary label="List Pane">
          <!-- Mobile settings menu — the rail gear opens this (listView='settings');
               each item drills into a settings section (Col 3). Desktop reaches
               settings via the sidebar, so it never lands on this state. -->
          <div v-if="listView === 'settings'" class="fg-settings-menu">
            <div class="fsm-head">
              <button class="fsm-back" type="button" @click="handleSelectView('chats')" aria-label="Back">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
              </button>
              <span class="fsm-title">Settings</span>
            </div>
            <button v-if="isAdmin" class="fsm-item" type="button" @click="handleSelectView('settings-inboxes')">
              <svg class="fsm-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              <span class="fsm-label">Chat-Accounts</span>
              <svg class="fsm-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
            <button v-if="isAdmin" class="fsm-item" type="button" @click="handleSelectView('settings-bots')">
              <svg class="fsm-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="8.5" cy="16" r="1.1"/><circle cx="15.5" cy="16" r="1.1"/><path d="M12 7v4"/><circle cx="12" cy="5" r="2"/></svg>
              <span class="fsm-label">Automations</span>
              <svg class="fsm-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
            <button class="fsm-item" type="button" @click="handleSelectView('settings-account')">
              <svg class="fsm-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span class="fsm-label">My Account</span>
              <svg class="fsm-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
            <button class="fsm-item" type="button" @click="handleSelectView('settings-help')">
              <svg class="fsm-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span class="fsm-label">Help &amp; Contact</span>
              <svg class="fsm-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
          </div>
          <!-- Mobile analytics menu — the rail chart icon opens this (listView='reports')
               for admins; each item drills into a report. Agents skip it (they go
               straight to Account performance, their only option). Desktop reaches
               reports via the sidebar accordion, so it never lands here. Reuses the
               settings-menu styles (.fsm-*). -->
          <div v-else-if="listView === 'reports'" class="fg-settings-menu">
            <div class="fsm-head">
              <button class="fsm-back" type="button" @click="handleSelectView('chats')" aria-label="Back">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
              </button>
              <span class="fsm-title">Analytics</span>
            </div>
            <button v-if="isAdmin" class="fsm-item" type="button" @click="handleSelectView('reports-custom')">
              <svg class="fsm-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>
              <span class="fsm-label">Custom reports</span>
              <svg class="fsm-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
            <button class="fsm-item" type="button" @click="handleSelectView('reports-accounts')">
              <svg class="fsm-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16"/><path d="M7 20v-5"/><path d="M12 20v-9"/><path d="M17 20v-13"/></svg>
              <span class="fsm-label">Account performance</span>
              <svg class="fsm-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
            <button v-if="isAdmin" class="fsm-item" type="button" @click="handleSelectView('reports-agents')">
              <svg class="fsm-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <span class="fsm-label">Agents performance</span>
              <svg class="fsm-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
          </div>
          <!-- Reports → Account performance: connected-Fangate-account list (Col 2,
               like Chats). Click an account → its analytics open in Col 3. -->
          <div v-else-if="isReportsAccounts" class="fg-rep-accts">
            <div class="fg-rep-accts-head">Account performance</div>
            <div class="fg-rep-accts-body">
              <div v-if="!connectedFangateAccounts.length" class="fg-rep-empty">
                <p class="fg-rep-empty-t">No Chat account with a Fangate account connected.</p>
                <button class="fg-rep-empty-cta" type="button" @click="handleSelectView('settings-inboxes')">Connect one in Chat-Accounts</button>
              </div>
              <button v-for="acc in connectedFangateAccounts" :key="'repacc-' + acc.id"
                      type="button" class="fg-rep-acct"
                      :class="{ active: selectedFangateInbox && Number(selectedFangateInbox.id) === Number(acc.id) }"
                      @click="handleSelectReportsAccount(acc)">
                <span class="fg-rep-acct-ava">{{ acctInitials(acc.name) }}</span>
                <span class="fg-rep-acct-name">{{ acc.name }}</span>
              </button>
            </div>
          </div>
          <FangateListPane
            v-else
            :inboxes="allInboxes"
            :agents="allAgents"
            :labels="allLabels"
            :selected-inbox-id="selectedInboxId"
            :selected-conversation-id="selectedConversationId"
            :mode="listMode"
            :view="listView"
            :selected-label="selectedLabel"
            :account-id="accountId"
            :unread-by-inbox="unreadByInbox"
            :unread-by-inbox-channels="unreadByInboxChannels"
            :total-by-inbox="totalByInbox"
            :total-by-inbox-channels="totalByInboxChannels"
            @select-inbox="handleSelectInbox"
            @select-conversation="handleSelectConversation"
            @back="handleBack"
            @open-agent-picker="openAgentPicker"
            @new-chat="handleNewChat"
            @select-label="handleSelectLabel"
            @select-view="handleSelectView"
          />
        </FangateBoundary>
      </div>

      <!-- Drag-to-resize handle between List Pane (Col 2) and Chat Area (Col 3).
           Only present when the list pane itself is visible (full-space views
           like Settings/Vault skip the list pane and therefore this divider). -->
      <div v-if="!isFullspaceView"
           class="fangate-col-divider"
           :class="{ 'is-dragging': dragging.active && dragging.target === 'list' }"
           role="separator"
           aria-orientation="vertical"
           :aria-valuenow="listWidth"
           :aria-valuemin="COLS.list.min"
           :aria-valuemax="COLS.list.max"
           title="Drag to resize the chat list — double-click to reset"
           @mousedown="e => startColDrag('list', e)"
           @dblclick="() => resetColWidth('list')">
        <span class="fangate-col-divider-grip" aria-hidden="true"></span>
      </div>

      <!-- ----------------------------------------------------------------
           COL 3 · Chat Area (flex-1) — Stage E3 hybrid
           Fangate-themed header + Chatwoot's MessagesView (bubbles/reply box)
           ---------------------------------------------------------------- -->
      <main class="fangate-chat" aria-label="Chat area">
        <!-- Mobile back to the settings menu now lives INSIDE FangateSettings'
             section header (beside the title), so there's no floating button here. -->

        <FangateBoundary v-if="isSettingsView" label="Settings">
          <FangateSettings
            :view="listView"
            :inboxes="allInboxes"
            :agents="allAgents"
            :labels="allLabels"
            :inbox-agent-map="inboxAgentMap"
            :is-mobile="isMobile"
            @open-agent-picker="openAgentPicker"
            @back="handleSelectView('settings')"
          />
        </FangateBoundary>
        <FangateBoundary v-else-if="isVaultView && selectedFangateInbox" label="Fangate Vault">
          <FangateVaultView
            :account="selectedFangateInbox"
            :is-mobile="isMobile"
            @close="handleVaultClose"
          />
        </FangateBoundary>
        <FangateBoundary v-else-if="isReportsAccounts" label="Account performance">
          <FangateTransactions v-if="selectedFangateInbox" :inbox-id="selectedFangateInbox.id" :is-mobile="isMobile" />
          <div v-else class="fg-rep-pick">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="m7 14 4-4 3 3 5-6"/></svg>
            <p>Select an account on the left to see its transactions &amp; analytics.</p>
          </div>
        </FangateBoundary>
        <FangateBoundary v-else-if="isReportsAgents" label="Agents performance">
          <FangateAgentsPerformance :account-id="accountId" :is-mobile="isMobile" />
        </FangateBoundary>
        <FangateBoundary v-else-if="isReportsCustom" label="Custom reports">
          <div class="fg-rep-soon">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>
            <p class="fg-rep-soon-t">Custom reports</p>
            <p class="fg-rep-soon-s">Build your own reports here — coming soon.</p>
          </div>
        </FangateBoundary>
        <!-- Admin-only Agents management page. isAdmin is double-checked
             inside the component too, so a non-admin landing here via a
             stale URL still hits a lock screen instead of the table. -->
        <FangateBoundary v-else-if="isAgentsView" label="Agents Management">
          <FangateAgentsManagement />
        </FangateBoundary>
        <FangateBoundary v-else label="Chat Area">
          <FangateChatArea
            :conversation-id="selectedConversationId"
            :conversations="selectedConversation ? [selectedConversation] : []"
            :labels="allLabels"
            :external-text="externalChatText"
            :external-text-nonce="externalNonce"
            :jump-message-id="jumpMessageId"
            :jump-nonce="jumpNonce"
            :is-mobile="isMobile"
            @back="mobileBack"
            @open-fangate="mobileFangateOpen = true"
          />
        </FangateBoundary>
      </main>

      <!-- ----------------------------------------------------------------
           COL 4 · Fangate Panel (collapsible right panel)
           #fangate-aside / #fangate-panel / #fangate-toggle all styled
           by _fangate-theme.scss — we only control the collapsed state here.
           Hidden entirely on Settings views (vault config etc. has nothing
           to do with the per-chat content/sales panel).
           ---------------------------------------------------------------- -->
      <!-- Mobile slide-over backdrop — tap to close the Fangate panel. -->
      <div v-if="isMobile && mobileFangateOpen"
           class="fg-mobile-backdrop"
           @click="mobileFangateOpen = false"></div>

      <aside
        v-show="showFangatePanel"
        id="fangate-aside"
        :class="panelCollapsed ? 'collapsed' : 'expanded'"
        aria-label="Fangate panel"
      >
        <!-- Grip toggle button — sits at left edge of the aside, visually
             between chat area and panel. z-index 10 keeps it above chat. -->
        <button
          id="fangate-toggle"
          :aria-label="panelCollapsed ? 'Expand Fangate panel' : 'Collapse Fangate panel'"
          @click="togglePanel"
        >
          <template v-if="panelCollapsed">
            <span class="toggle-label">FANGATE</span>
          </template>
          <template v-else>
            <span class="grip-line"></span>
            <span class="grip-line"></span>
            <span class="grip-line"></span>
          </template>
        </button>

        <!-- The sliding panel itself — Fangate integration (Content + Sales) -->
        <div id="fangate-panel">
          <div id="fangate-content">
            <FangateBoundary label="Fangate Panel">
              <FangatePanel
                :active-inbox="selectedInbox"
                :conversation-id="selectedConversationId"
                @insert-into-chat="handleInsertIntoChat"
              />
            </FangateBoundary>
          </div><!-- /#fangate-content -->
        </div><!-- /#fangate-panel -->
      </aside>

    </div><!-- /.fangate-body -->

    <!-- ================================================================
         SHARED ACCOUNT-AGENT PICKER (position:fixed, opened via right-click
         on account rows in ListPane or nav-items in Sidebar)
         ================================================================ -->
    <div
      v-if="agentPicker.show"
      class="fg-agent-picker"
      :style="{ left: agentPicker.x + 'px', top: agentPicker.y + 'px' }"
      @click.stop
    >
      <div class="fg-agent-picker-head">
        <div class="fg-agent-picker-title">(Un-)Assign an Agent</div>
        <div class="fg-agent-picker-sub">
          for account <strong>{{ agentPicker.inbox?.name }}</strong>
        </div>
      </div>
      <div class="ctx-sep"></div>
      <button
        v-for="agent in assignableAgents" :key="agent.id"
        class="ctx-item"
        :style="isAgentAssigned(agentPicker.inbox?.id, agent.id)
          ? 'color:var(--accent-strong);background:var(--accent-tint);'
          : ''"
        @click="toggleInboxAgent(agentPicker.inbox?.id, agent.id)"
      >
        <span class="fg-agent-chk" :class="{ on: isAgentAssigned(agentPicker.inbox?.id, agent.id) }">
          <svg v-if="isAgentAssigned(agentPicker.inbox?.id, agent.id)"
               width="8" height="8" viewBox="0 0 12 12"
               fill="none" stroke="white" stroke-width="2.5">
            <polyline points="2,6 5,9 10,3"/>
          </svg>
        </span>
        👤 {{ agent.name }}
      </button>
      <div v-if="!assignableAgents.length"
           class="ctx-item" style="color:var(--ink-muted);pointer-events:none;">
        No agents available
      </div>
      <div class="ctx-sep"></div>
      <button class="ctx-item" style="color:var(--ink-muted);"
              @click="toggleInboxAgent(agentPicker.inbox?.id, null); closeAgentPicker()">
        — Unassign
      </button>
    </div>

    <!-- "+ New chat" composer modal (#143) — teleported to body inside
         the component, so it overlays everything cleanly. Shown when the
         chat list pane's "+ New" button fires @new-chat. -->
    <FangateNewChatModal
      v-model:open="newChatModalOpen"
      :inbox-id="newChatInboxId"
      :inboxes="allInboxes"
      @select-conversation="handleNewChatPicked"
    />

    <!-- First-run onboarding tour — transparent iframe over the loaded dashboard.
         The tour (public/onboarding-intro.html) dims the dashboard via its own
         overlay and posts 'cc-onboarding-done' on Skip/Finish to close. -->
    <div v-if="showOnboarding" class="cc-onboarding-overlay">
      <iframe src="/onboarding-intro.html" title="Welcome to Creator-Chat — tour"
              class="cc-onboarding-frame"></iframe>
    </div>

    <!-- Branded confirm dialog host — replaces native window.confirm() app-wide -->
    <FangateConfirmHost />

  </div><!-- /.fangate-shell -->
</template>

<style lang="scss" scoped>
// ============================================================================
// Structural layout — widths, heights, flex geometry.
// Component-level look (colors, fonts, shadows) lives in _fangate-theme.scss.
// ============================================================================

// Root wrapper: full viewport, column flex
.fangate-root {
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

// ---- Topbar -----------------------------------------------------------------
.fangate-topbar {
  height: 72px;
  min-height: 72px;
  flex-shrink: 0;
  background: #131e1e;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 20px;
  gap: 20px;
  z-index: 10;
  position: relative;

  .topbar-center {
    flex: 1;
  }

  .topbar-right {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-right: 24px;
    position: relative;
  }

  // Notification bell + dropdown — inline (no separate page navigation).
  .tb-bell-wrap {
    position: relative;
  }
  .tb-bell {
    background: rgba(255, 255, 255, 0.06);
    border: none;
    color: #f5efe2;
    width: 36px; height: 36px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    position: relative;
    transition: background 0.12s;
    &:hover { background: rgba(255, 255, 255, 0.14); }
  }
  // Co-creator consent indicator — teal accent so it reads as distinct from the
  // red chat-notification bell.
  .tb-consent-wrap {
    position: relative;
    margin-right: 8px;
    display: flex;
  }
  .tb-consent {
    background: rgba(16, 185, 129, 0.16);
    border: none;
    color: #6ee7c7;
    width: 36px; height: 36px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    position: relative;
    transition: background 0.12s;
    &:hover { background: rgba(16, 185, 129, 0.28); }
  }
  .tb-consent-panel {
    position: absolute; top: calc(100% + 8px); right: 0;
    width: 320px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 14px 32px rgba(20, 10, 60, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    z-index: 200;
    color: #1a0a6b;
  }
  .tb-consent-head {
    padding: 12px 14px;
    font-family: 'Libre Baskerville', serif;
    font-size: 13px; font-weight: 700;
    color: #1a0a6b;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
  .tb-consent-item {
    padding: 12px 14px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    &:last-child { border-bottom: none; }
  }
  .tb-consent-text {
    font-family: 'Inter', sans-serif;
    font-size: 12.5px;
    line-height: 1.45;
    color: #374151;
    strong { color: #1a0a6b; font-weight: 700; }
  }
  .tb-consent-link {
    color: #0d9488;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    &:hover { color: #0b7d72; }
  }
  .tb-consent-actions {
    display: flex; gap: 8px;
    margin-top: 10px;
  }
  .tb-consent-accept {
    flex: 1;
    background: #0d9488; color: #fff;
    border: none; border-radius: 8px;
    padding: 7px 10px;
    font-family: 'Inter', sans-serif;
    font-size: 12.5px; font-weight: 600;
    cursor: pointer;
    transition: background 0.12s;
    &:hover { background: #0b7d72; }
    &:disabled { opacity: 0.6; cursor: default; }
  }
  .tb-consent-decline {
    flex: 1;
    background: #f3f4f6; color: #4b5563;
    border: 1px solid rgba(0, 0, 0, 0.08); border-radius: 8px;
    padding: 7px 10px;
    font-family: 'Inter', sans-serif;
    font-size: 12.5px; font-weight: 600;
    cursor: pointer;
    transition: background 0.12s;
    &:hover { background: #e5e7eb; }
  }
  .tb-consent-badge {
    position: absolute; top: -3px; right: -3px;
    min-width: 16px; height: 16px; padding: 0 4px;
    border-radius: 999px;
    background: #0d9488; color: #fff;
    font-family: 'Inter', sans-serif;
    font-size: 10px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
  }
  .tb-bell-badge {
    position: absolute; top: -3px; right: -3px;
    min-width: 16px; height: 16px; padding: 0 4px;
    border-radius: 999px;
    background: #dc2626; color: #fff;
    font-family: 'Inter', sans-serif;
    font-size: 10px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    line-height: 1;
  }
  .tb-bell-panel {
    position: absolute; top: calc(100% + 8px); right: 0;
    width: 340px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 14px 32px rgba(20, 10, 60, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    z-index: 200;
    color: #1a0a6b;
  }
  .tb-bell-head {
    padding: 12px 14px;
    font-family: 'Libre Baskerville', serif;
    font-size: 13px; font-weight: 700;
    color: #1a0a6b;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
  .tb-bell-empty {
    padding: 20px 14px;
    font-family: 'Inter', sans-serif;
    font-size: 12.5px;
    color: #6b7280;
    text-align: center;
  }
  .tb-bell-list {
    max-height: 380px;
    overflow-y: auto;
  }
  .tb-bell-item {
    padding: 10px 14px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
    cursor: pointer;
    &:last-child { border-bottom: none; }
    &.is-unread { background: rgba(91, 46, 207, 0.04); }
    &:hover { background: rgba(91, 46, 207, 0.07); }
  }
  .tb-bell-item-title {
    font-family: 'Inter', sans-serif;
    font-size: 12.5px; font-weight: 600;
    color: #1a0a6b;
    line-height: 1.35;
    margin-bottom: 2px;
  }
  .tb-bell-item-time {
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    color: #6b7280;
  }
  .tb-bell-clear {
    width: 100%;
    padding: 11px 14px;
    background: rgba(91, 46, 207, 0.05);
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    font-family: 'Inter', sans-serif;
    font-size: 12px; font-weight: 700;
    color: #5b2ecf;
    cursor: pointer;
    text-align: center;
    transition: background 0.12s;
    &:hover { background: rgba(91, 46, 207, 0.12); }
  }

  // Name + role stack
  .tb-user-text {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    line-height: 1.1;
    gap: 3px;
  }
  .tb-user-name {
    font-family: 'Libre Baskerville', serif;
    font-size: 13px;
    font-weight: 600;
    color: #f5efe2;
    white-space: nowrap;
  }
  .tb-user-role {
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #5eead4;
  }

  // Avatar with status dot
  .tb-avatar-wrap {
    position: relative;
    cursor: pointer;
  }
  .tb-avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 1.5px solid #2a3838;
    background: rgba(255, 255, 255, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    font-family: 'Libre Baskerville', serif;
    font-size: 14px;
    font-weight: 700;
    color: #f5efe2;
    transition: border-color 0.15s, background 0.15s;
    img { width: 100%; height: 100%; object-fit: cover; }
    &:hover {
      border-color: #14b8a6;
      background: rgba(20, 184, 166, 0.10);
    }
  }
  .tb-status-dot {
    position: absolute;
    bottom: -1px;
    right: -1px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: #10b981;
    border: 2px solid #1f2a2a;   // matches topbar bg
    pointer-events: none;
    &--off { background: #6b7280; }
  }

  // Dropdown menu
  .tb-user-menu {
    position: absolute;
    top: 54px;
    right: 24px;
    min-width: 220px;
    background: var(--bg-panel);
    border: 1px solid var(--border-soft);
    border-radius: 12px;
    box-shadow: 0 8px 28px rgba(11, 20, 20, 0.25);
    padding: 6px;
    z-index: 1000;
  }
  .tb-user-menu-head {
    padding: 8px 12px 6px;
  }
  .tb-user-menu-name {
    font-family: 'Libre Baskerville', serif;
    font-size: 13px;
    font-weight: 700;
    color: var(--ink-strong);
    line-height: 1.2;
  }
  .tb-user-menu-email {
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    color: var(--ink-muted);
    margin-top: 2px;
    line-height: 1.2;
    word-break: break-all;
  }
}

// ---- Body row ---------------------------------------------------------------
.fangate-body {
  flex: 1;
  display: flex;
  flex-direction: row;
  min-height: 0;
  overflow: hidden;
}

// ---- Col 1: Nav sidebar (260 px default; resizable [170..320] via inline
// style bound from FangateLayout.sidebarWidth — see startColDrag('sidebar')) -
.fangate-nav {
  width: 260px;       // fallback; overridden by inline :style
  flex-shrink: 0;
  background: var(--bg-app);
  border-right: 1px solid var(--border-soft);
  box-shadow: 4px 0 14px -10px rgba(74, 53, 32, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .fangate-nav-scroll {
    flex: 1;
    overflow: hidden; // FangateSidebar handles its own scroll internally
    display: flex;
    flex-direction: column;
  }
}

// ---- Sidebar↔List & List↔Chat dividers (drag-to-resize handles) -----------
// 3px wide invisible hit zone — comfortable click target without becoming a
// heavyweight visual. On hover (or while dragging) the strip fills with the
// accent tint + a 2px-wide vertical grip pops in centred; the column-resize
// cursor activates.
.fangate-col-divider {
  width: 3px;
  flex-shrink: 0;
  cursor: col-resize;
  position: relative;
  background: transparent;
  z-index: 5;       // sits above adjacent borders so hover always wins
  transition: background 0.12s;

  &:hover,
  &.is-dragging {
    background: var(--accent-tint);
    .fangate-col-divider-grip { opacity: 1; }
  }
  &.is-dragging { background: var(--accent-ring); }
}
.fangate-col-divider-grip {
  position: absolute;
  left: 0.5px;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 26px;
  border-radius: 1px;
  background: var(--accent);
  opacity: 0;
  transition: opacity 0.12s;
  pointer-events: none;  // don't intercept the mousedown on the strip
}

// ---- Col 2: List pane (270 px default; resizable [220..380] via inline
// style bound from FangateLayout.listWidth — see startColDrag('list', ...)) -
.fangate-list {
  width: 270px;       // fallback; overridden by inline :style
  flex-shrink: 0;
  background: var(--bg-middle);
  border-right: 1px solid var(--border-soft);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

// Layer head (back button + title)
.list-layer-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px 8px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-soft);
}

.list-layer-title {
  font-family: 'Libre Baskerville', serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-strong);
  letter-spacing: 0.03em;
}

.list-layer-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

// Tab row inside list pane
.list-tabs {
  display: flex;
  gap: 4px;
  padding: 6px 4px 8px;
}

// Back button (layer 1 → layer 0)
.back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.12s, border-color 0.12s;

  &:hover {
    color: var(--ink-strong);
    border-color: var(--border-medium);
  }
}

// Account-row meta
.account-row-meta {
  flex: 1;
  min-width: 0;
}

.account-row-name {
  font-family: 'Libre Baskerville', serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-row-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: var(--ink-muted);
  margin-top: 1px;
}

// Chat-row body / meta
.chat-row-body {
  flex: 1;
  min-width: 0;
}

.chat-row-name {
  font-family: 'Libre Baskerville', serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-row-preview {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--ink-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 1px;
}

.chat-row-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.chat-row-time {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: var(--ink-muted);
}

// ---- Col 3: Chat area (flex-1) -----------------------------------------------
.fangate-chat {
  flex: 1;
  min-width: 0;
  background: var(--bg-main);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.chat-empty-icon {
  font-size: 36px;
  opacity: 0.35;
}

.chat-empty-label {
  font-family: 'Libre Baskerville', serif;
  font-size: 15px;
  color: var(--ink-muted);
  font-weight: 600;
}

.chat-empty-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: var(--ink-muted);
  opacity: 0.55;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

// ---- Col 4: Fangate aside — structural overrides ---------------------------
// The visual look of #fangate-aside / #fangate-panel / #fangate-toggle
// comes entirely from _fangate-theme.scss. We only ensure it stretches full height.
#fangate-aside {
  align-self: stretch;
}

// Fangate tab bar
.fg-tab-bar {
  display: flex;
  gap: 4px;
  padding: 10px 12px 6px;
  background: rgba(74, 53, 32, 0.06);
  border-bottom: 1px solid var(--border-soft);
  flex-shrink: 0;
}

// Fangate panel body
.fg-panel-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 16px;
}

// ---- Shared placeholder text ------------------------------------------------
.fg-placeholder {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: var(--ink-muted);
  text-align: center;
  line-height: 1.6;
}

// ---- Account-agent picker (position:fixed overlay) --------------------------
.cc-onboarding-overlay { position: fixed; inset: 0; z-index: 99999; }
.cc-onboarding-frame { width: 100%; height: 100%; border: 0; background: transparent; display: block; }
.fg-agent-picker {
  position: fixed;
  z-index: 9998;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(31, 42, 42, 0.14);
  padding: 6px;
  min-width: 210px;
}
.fg-agent-picker-head {
  padding: 8px 12px 6px;
}
.fg-agent-picker-title {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--accent-strong);
}
.fg-agent-picker-sub {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: var(--ink-muted);
  margin-top: 2px;
}
.fg-agent-chk {
  display: inline-flex;
  width: 13px;
  height: 13px;
  border-radius: 3px;
  flex-shrink: 0;
  border: 1.5px solid var(--border-medium);
  background: transparent;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
  &.on { border-color: var(--accent); background: var(--accent); }
}

// ---- Reports → Account performance: connected-account list (Col 2) ----------
// GLOBAL (desktop + mobile) — mirrors .list-layer-head / .list-layer-body so the
// reports account picker matches the chats list column. These previously lived
// ONLY under .is-mobile, which left the desktop Col-2 list completely unstyled
// (header flush to the sidebar, avatar not a circle → "SSven", no active state).
.fg-rep-accts { display: flex; flex-direction: column; height: 100%; overflow: hidden; }
.fg-rep-accts-head {
  flex-shrink: 0;
  padding: 16px 16px 12px;
  border-bottom: 1px solid var(--border-soft);
  font-family: 'Libre Baskerville', serif;
  font-size: 17px; font-weight: 700; letter-spacing: 0.01em;
  color: var(--ink-strong);
  // Reactive to the resizable Col-2 width: truncate, never overflow, when narrow.
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fg-rep-accts-body { flex: 1; overflow-y: auto; padding: 8px; display: flex; flex-direction: column; gap: 3px; }
.fg-rep-acct {
  display: flex; align-items: center; gap: 10px; width: 100%; text-align: left;
  border: none; background: transparent; border-radius: 10px; padding: 8px 10px;
  cursor: pointer; font-family: 'Inter', sans-serif; transition: background 0.12s;
}
.fg-rep-acct:hover { background: var(--bg-subtle); }
.fg-rep-acct.active { background: var(--accent-tint, rgba(13, 148, 136, 0.14)); }
.fg-rep-acct-ava {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font: 600 11px 'Inter', sans-serif; color: #fff; background: var(--accent, #0d9488);
}
.fg-rep-acct.active .fg-rep-acct-ava { background: var(--accent-strong, #0f766e); }
.fg-rep-acct-name { font: 500 13.5px 'Inter', sans-serif; color: var(--ink-strong); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fg-rep-acct.active .fg-rep-acct-name { color: var(--accent-strong, #0f766e); font-weight: 600; }
.fg-rep-empty { padding: 22px 14px; text-align: center; }
.fg-rep-empty-t { font-size: 13px; color: var(--ink-muted); margin: 0 0 12px; line-height: 1.5; }
.fg-rep-empty-cta { border: 1px solid var(--accent, #0d9488); background: transparent; color: var(--accent-strong, #0f766e); border-radius: 9px; padding: 7px 14px; font: 500 12.5px 'Inter', sans-serif; cursor: pointer; }
.fg-rep-empty-cta:hover { background: var(--accent-tint, rgba(13, 148, 136, 0.14)); }
// Reports Col-3 placeholders (pick-an-account / custom coming-soon)
.fg-rep-pick, .fg-rep-soon { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; padding: 40px; text-align: center; color: var(--ink-muted); }
.fg-rep-pick svg, .fg-rep-soon svg { color: var(--border-medium); }
.fg-rep-pick p { font-size: 14px; max-width: 320px; line-height: 1.55; margin: 0; }
.fg-rep-soon-t { font: 400 18px 'Libre Baskerville', Georgia, serif; color: var(--ink-strong); margin: 0; }
.fg-rep-soon-s { font-size: 13px; margin: 0; }

// ===== Mobile (≤768px) — single-pane drill-down (Stage 1a) ===================
// Everything is scoped under .is-mobile, so the desktop 4-column layout is
// completely untouched. One pane shows at a time: the list, or (when a chat is
// open / a full-space view is active) the main column.
.fangate-root.is-mobile {
  // Mobile has no hover → flash the tapped element so taps feel responsive ("wo getippt wird").
  -webkit-tap-highlight-color: rgba(13, 148, 136, 0.20);
  // Stage 1b — the dark full-width topbar is removed from the DOM on phones
  // (v-if="!isMobile"); its pieces (logo, bells, admin avatar) live in the slim
  // icon rail instead. The desktop 260px nav + drag dividers are hidden too.
  .fangate-nav,
  .fangate-col-divider { display: none; }

  // The list pane fills the screen MINUS the 52px rail.
  .fangate-list {
    width: auto !important;
    min-width: 0 !important;
    flex: 1 1 0%;
    border-right: none;
  }

  // Fangate panel (Col 4) → hidden by default on mobile; the chat-header FG
  // pill opens it as a right slide-over (Stage 1c). The desktop collapse-grip
  // has no place here, so it's hidden too; backdrop-tap closes the panel.
  #fangate-aside { display: none; }
  #fangate-toggle { display: none; }
  &.m-fangate-open {
    #fangate-aside {
      display: flex !important;
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      width: 88% !important;
      max-width: 420px;
      min-width: 0 !important;
      z-index: 1200;
      box-shadow: -10px 0 34px rgba(20, 20, 30, 0.30);
    }
    #fangate-panel { width: 100% !important; }
    .mbell-strip { display: none; }   // bells must not float over the open Fangate slide-over
  }
  .fg-mobile-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(20, 20, 30, 0.45);
    z-index: 1199;
  }

  // Default = the LIST is shown, the chat/full-space column is hidden.
  .fangate-chat { display: none; }

  // A conversation is open OR a full-space view is active → the main column
  // takes over full-width and the list is hidden.
  &.m-main {
    .fangate-list { display: none; }
    .fangate-chat { display: flex; }
  }

  // The rail STAYS visible while a conversation is open — the agent keeps the
  // sidebar in view. The composer stays column-width (confirmed): the phone's
  // OS keyboard is full-width anyway, so the input doesn't need to overlay it.

  // ---- Mobile icon rail (Stage 1b) -----------------------------------------
  // Slim persistent 52px sand-coloured left column. padding-top clears the
  // floating bell strip. Order: logo · active account · divider · sections.
  .fangate-mobile-rail {
    flex: 0 0 52px;
    width: 52px;
    min-width: 52px;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    padding: 12px 0 14px;
    background: #e7e0d1;
    border-right: 1px solid var(--border-medium);
    border-radius: 16px 16px 0 0;   // soft rounded top (modern panel look)
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 30;
  }
  .mrail-logo { display: block; width: 32px; height: 32px; margin-bottom: 6px; flex-shrink: 0; }
  // Active account (top): round avatar + Telegram badge + green name.
  .mrail-acc { border: none; background: transparent; cursor: pointer; padding: 0; position: relative; flex-shrink: 0; }
  .mrail-acc-av {
    width: 38px; height: 38px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-family: 'Libre Baskerville', serif; font-weight: 700; font-size: 15px;
    border: 2px solid var(--accent);
    background: linear-gradient(135deg, #43e97b, #0d9488);
    overflow: hidden;
    img { width: 100%; height: 100%; object-fit: cover; }
  }
  .mrail-acc-badge {
    position: absolute; bottom: -2px; right: -2px; width: 16px; height: 16px; border-radius: 50%;
    background: #2aabee; color: #fff; display: flex; align-items: center; justify-content: center;
    border: 1.5px solid #e7e0d1;
    svg { width: 10px; height: 10px; }
  }
  // Unread badge for OTHER accounts — top-right of the account avatar (the
  // Telegram badge sits bottom-right, so no clash).
  .mrail-acc-unread {
    position: absolute; top: -4px; right: -4px;
    min-width: 16px; height: 16px; padding: 0 3px;
    background: var(--red, #c0392b); color: #fff;
    border-radius: 8px; border: 2px solid #e7e0d1;
    font-size: 9px; font-weight: 700; line-height: 1;
    display: flex; align-items: center; justify-content: center;
  }
  .mrail-acc-name {
    font-size: 8.5px; font-weight: 800; color: var(--accent-strong);
    margin: 1px 0 2px; max-width: 50px; text-align: center;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex-shrink: 0;
  }
  .mrail-divider { width: 26px; height: 1px; background: var(--border-medium); margin: 6px 0 8px; flex-shrink: 0; }
  .mrail-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 42px; height: 42px; flex-shrink: 0;
    border: none; border-radius: 12px; background: transparent;
    color: var(--ink-medium); cursor: pointer; position: relative;
    transition: background 0.12s, color 0.12s, transform 0.08s;
    svg { width: 22px; height: 22px; }
    &:hover { background: rgba(74, 53, 32, 0.10); }
    &:active { transform: scale(0.88); }
    &.is-active {
      background: rgba(13, 148, 136, 0.16);
      color: var(--accent-strong);
      box-shadow: inset 3px 0 0 var(--accent);
    }
  }
  .mrail-fg-btn {
    width: 48px; height: 48px;
    background: transparent;
    svg { width: 44px; height: 44px; }
    &:hover { background: rgba(91, 46, 207, 0.10); }
    &:active { transform: scale(0.9); }
    &.is-active { background: rgba(91, 46, 207, 0.12); box-shadow: inset 3px 0 0 var(--fangate-purple, #5b2ecf); }
  }
  // Unread badge on a rail section icon (Chats / Channels).
  .mrail-dot {
    position: absolute; top: 1px; right: 1px;
    min-width: 15px; height: 15px; padding: 0 3px;
    background: var(--accent); color: #fff;
    border-radius: 8px; border: 1.5px solid #e7e0d1;
    font-size: 9px; font-weight: 700; line-height: 1;
    display: flex; align-items: center; justify-content: center;
  }

  // Columns clear the floating bell strip (40px) so their headers + right-side
  // controls never sit under the bells.
  .fangate-list { padding-top: 40px; }
  .fangate-chat { padding-top: 40px; }

  // ---- Mobile bell strip (Stage 1b) ----------------------------------------
  // Floats at the very top-right over the content; only the buttons + open
  // popovers are interactive (pointer-events:none on the strip itself).
  .mbell-strip {
    position: fixed;
    top: 0; left: 52px; right: 0;   // starts after the rail so the logo stays clear
    height: 40px;
    display: flex; align-items: center; justify-content: flex-end;
    gap: 6px;
    padding: 0 14px 0 0;          // vertically centered in the 40px band
    z-index: 1400;
    pointer-events: none;
    // Frosted bar — translucent cream + blur so content scrolls under it (iOS-like).
    // Fallback bg is fairly OPAQUE on purpose: iOS Chrome (WKWebView) often fails
    // to paint backdrop-filter on a fixed element after a reload (shows white/
    // broken until a repaint) — at this opacity a glitch reads as cream, not white.
    background: rgba(243, 240, 233, 0.88);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(74, 53, 32, 0.07);
    // Pin to its own GPU layer so it survives iOS Chrome's reload reflow without
    // breaking/flashing white.
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
    will-change: transform;
  }
  .mbell-wrap, .mbell-avatar { pointer-events: auto; position: relative; }
  .mbell-btn {
    width: 34px; height: 34px; border-radius: 10px;
    background: rgba(251, 248, 241, 0.94);
    border: 1px solid var(--border-soft);
    display: inline-flex; align-items: center; justify-content: center;
    color: var(--ink-medium); cursor: pointer; position: relative;
    box-shadow: 0 2px 6px rgba(11, 20, 20, 0.12);
    svg { width: 19px; height: 19px; }
    &:hover { background: #fff; }
  }
  .mbell-badge {
    position: absolute; top: -5px; right: -5px; min-width: 15px; height: 15px;
    border-radius: 8px; font-size: 9px; font-weight: 700; color: #fff;
    display: flex; align-items: center; justify-content: center; padding: 0 3px;
    border: 1.5px solid var(--bg-panel);
    &.red { background: var(--red, #c0392b); }
    &.teal { background: var(--accent); }
  }
  .mbell-avatar .tb-avatar {
    width: 34px; height: 34px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    overflow: hidden; box-shadow: 0 2px 6px rgba(11, 20, 20, 0.12);
    img { width: 100%; height: 100%; object-fit: cover; }
  }
  .mbell-pop {
    position: fixed; top: 44px; right: 10px;
    width: min(84vw, 300px);
    background: var(--bg-panel); border: 1px solid var(--border-soft);
    border-radius: 14px; box-shadow: 0 14px 44px rgba(11, 20, 20, 0.28);
    z-index: 1410; overflow: hidden; pointer-events: auto;
  }
  .mbell-pop-head { display: flex; align-items: center; justify-content: space-between; padding: 11px 13px; border-bottom: 1px solid var(--border-soft); }
  .mbell-pop-title { font-family: 'Libre Baskerville', serif; font-size: 13px; font-weight: 700; color: var(--ink-strong); }
  .mbell-pop-clear { font-size: 10.5px; font-weight: 600; color: var(--accent-strong); cursor: pointer; border: none; background: transparent; }
  .mbell-pop-body { max-height: 60vh; overflow-y: auto; }
  .mbell-pop-item {
    display: flex; gap: 10px; padding: 10px 13px;
    border-bottom: 1px solid var(--border-soft); cursor: pointer;
    &:last-child { border-bottom: none; }
    &:hover { background: var(--bg-subtle); }
    &.is-unread { background: rgba(13, 148, 136, 0.05); }
  }
  .mbell-ic {
    width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    svg { width: 15px; height: 15px; }
    &.msg { background: rgba(13, 148, 136, 0.14); color: var(--accent-strong); }
    &.tag { background: rgba(91, 46, 207, 0.12); color: var(--fangate-purple, #5b2ecf); }
  }
  .mbell-tx { flex: 1; min-width: 0; }
  .mbell-it-title { font-size: 12px; font-weight: 600; color: var(--ink-strong); line-height: 1.35; }
  .mbell-it-time { font-size: 9.5px; color: var(--ink-muted); margin-top: 3px; }
  .mbell-tag-acts { display: flex; gap: 6px; margin-top: 7px; }
  .mbell-tag-acts button { font-size: 10.5px; font-weight: 600; padding: 4px 11px; border-radius: 999px; border: none; cursor: pointer; }
  .mbell-acc { background: var(--accent); color: #fff; }
  .mbell-dec { background: var(--bg-subtle); border: 1px solid var(--border-soft); color: var(--ink-medium); }
  .mbell-empty { padding: 26px 16px; text-align: center; font-size: 12px; color: var(--ink-muted); }
  .mbell-user-menu {
    position: fixed !important; top: 44px !important; right: 10px !important;
    left: auto !important; bottom: auto !important; z-index: 1410 !important;
    pointer-events: auto;   // parent .mbell-strip is pointer-events:none → re-enable, else the menu items (My Account / Sign out) aren't clickable
    // The desktop .tb-user-menu box styling is nested under .fangate-topbar
    // (gone on mobile), so the mobile menu must carry the box itself.
    min-width: 220px;
    background: var(--bg-panel);
    border: 1px solid var(--border-soft);
    border-radius: 12px;
    box-shadow: 0 8px 28px rgba(11, 20, 20, 0.25);
    padding: 6px;
    .tb-user-menu-head { padding: 8px 12px 6px; }
    .tb-user-menu-name { font-family: 'Libre Baskerville', serif; font-size: 13px; font-weight: 700; color: var(--ink-strong); line-height: 1.2; }
    .tb-user-menu-email { font-family: 'Inter', sans-serif; font-size: 11px; color: var(--ink-muted); margin-top: 2px; line-height: 1.2; word-break: break-all; }
  }

  // Reports → Account performance styling is now GLOBAL (see .fg-rep-* above the
  // mobile block) so the desktop Col-2 list is styled too — no mobile-only copy.

  // ---- Mobile settings menu (rail gear → listView 'settings') --------------
  // Drill-down list level in Col 2; each item opens a settings section in Col 3.
  // Transparent → keeps the cream list-column background (--bg-middle), so it
  // matches the channels list and the area below the last item stays consistent.
  .fg-settings-menu {
    display: flex; flex-direction: column;
    height: 100%;
    background: transparent;
  }
  .fsm-head {
    display: flex; align-items: center; gap: 8px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--border-soft);
    flex-shrink: 0;
  }
  .fsm-back {
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; border: none; border-radius: 9px;
    background: transparent; color: var(--ink-strong); cursor: pointer;
    margin-left: -6px; flex-shrink: 0;
  }
  .fsm-title { font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 700; color: var(--ink-strong); letter-spacing: 0.02em; }
  .fsm-item {
    display: flex; align-items: center; gap: 12px;
    padding: 15px 16px; margin: 10px 12px;
    border: 1px solid var(--border-soft); border-radius: 12px;
    background: var(--bg-panel);          // near-white card — stands out from the cream menu
    color: var(--ink-strong); cursor: pointer;
    font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 500;
    text-align: left;
    box-shadow: 0 1px 3px rgba(31, 42, 42, 0.05);
    transition: background 0.12s, transform 0.08s;
    &:hover { background: var(--bg-subtle); }
    &:active { transform: scale(0.99); background: var(--bg-subtle); }
    .fsm-ic { width: 20px; height: 20px; color: var(--accent-strong); flex-shrink: 0; }
    .fsm-label { flex: 1; min-width: 0; }
    .fsm-chev { width: 18px; height: 18px; color: var(--ink-muted); flex-shrink: 0; }
  }
  // Floating back button: from a settings section back to the settings menu.
  .fg-fs-back {
    position: fixed; top: 5px; left: 58px; z-index: 1450;   // above the bell strip (1400) so it isn't occluded
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; border: none; border-radius: 10px;
    background: var(--bg-panel); color: var(--ink-strong); cursor: pointer;
    box-shadow: 0 1px 4px rgba(31, 42, 42, 0.14);
  }

  // Press feedback for the remaining interactive bits (icon-buttons scale; rows tint).
  .mrail-acc, .mbell-btn, .mbell-avatar, .fsm-back, .fg-fs-back { transition: transform 0.08s; }
  .mrail-acc:active, .mbell-btn:active, .mbell-avatar:active, .fsm-back:active, .fg-fs-back:active { transform: scale(0.9); }
  .fsm-item:active { background: var(--border-soft); }

  // No-account empty state — covers the content area (right of the 52px rail,
  // below the 40px bell strip) so the rail + bells stay usable.
  .m-noacct {
    position: fixed; top: 40px; left: 52px; right: 0; bottom: 0; z-index: 6;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 12px; padding: 32px 28px; text-align: center;
    background: var(--bg-main);
  }
  .m-noacct-icon { color: var(--ink-muted); opacity: 0.45; }
  .m-noacct-title { font-family: 'Libre Baskerville', serif; font-size: 17px; color: var(--ink-strong); margin: 0; }
  .m-noacct-text { font-size: 13.5px; color: var(--ink-muted); margin: 0; max-width: 290px; line-height: 1.5; }
  .m-noacct-btn {
    margin-top: 6px; display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 18px; border-radius: 999px; border: none;
    background: var(--accent); color: #fff; font-size: 14px; font-weight: 600; cursor: pointer;
    box-shadow: 0 1px 3px rgba(13, 148, 136, 0.3);
    transition: transform 0.08s;
    &:active { transform: scale(0.97); }
  }
}
</style>
